import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftRight,
  BadgeCheck,
  Bike,
  Keyboard,
  MapPin,
  Printer,
  ScanBarcode,
  Trash2,
  TriangleAlert,
  UserRound,
  Wallet,
} from 'lucide-react'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { ZERO_CASH, normalizeTerminalId } from '@aeris/cash'
import type {
  PosCheckoutResponse,
  PosMatch,
  PosPricingSnapshot,
  SalesDocumentDetail,
} from '@aeris/contracts'
import {
  MONEY_SCALE,
  QUANTITY_SCALE,
  ZERO_MONEY,
  add,
  compare,
  formatMoney,
  parseHumanAmount,
  toString,
  zero,
  type Decimal,
} from '@aeris/money'
import { partyDisplayName } from '@aeris/party'
import { formatDocumentNumber } from '@aeris/printing'
import type { PaperWidth } from '@aeris/printing'
import { amountDiscount, computeTotals, percentDiscount } from '@aeris/sales'
import {
  Badge,
  Button,
  cn,
  ConfirmDialog,
  EmptyState,
  Field,
  InlineAlert,
  Input,
  MoneyInput,
  Page,
  PageHeader,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  useScopedKeydown,
  VisuallyHidden,
} from '@aeris/ui'

import { AuthorisationDialog } from '../components/authorisation-dialog.tsx'
import { ConfirmPersonDialog } from '../components/confirm-person-dialog.tsx'
import { CourierDialog } from '../components/courier-dialog.tsx'
import { EdgePairing } from '../components/edge-pairing.tsx'
import { LineThumb } from '../components/line-thumb.tsx'
import { NewExchangeDialog } from '../components/new-exchange-dialog.tsx'
import { PosCustomerDialog } from '../components/pos-customer-dialog.tsx'
import {
  PosPaymentDialog,
  type PosTender,
} from '../components/pos-payment-dialog.tsx'
import { QuoteDialog } from '../components/quote-dialog.tsx'
import {
  ReceiptDialog,
  type ProvisionalReceipt,
} from '../components/receipt-dialog.tsx'
import { TerminalSelect } from '../components/terminal-select.tsx'
import { fetchCustomerCredit } from '../lib/api/exchanges.ts'
import {
  ApiError,
  ApiUnreachableError,
  fetchCompany,
  fetchCurrentCashSession,
  fetchCredit,
  fetchPaymentMethods,
  fetchParties,
  fetchPriceLists,
  fetchSalespeople,
  openCashSession,
  posCancelSale,
  posCheckout,
  posLookup,
  posOrder,
  posQuote,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { useBalcaoAnterior } from '../lib/balcao-anterior.ts'
import { customerLookup } from '../lib/customer-lookup.ts'
import {
  EdgeError,
  edgeCheckout,
  edgeCurrentSession,
  edgeLookup,
  edgeOpenSession,
  edgePaymentMethods,
  toPaymentMethodSummary,
} from '../lib/edge-api.ts'
import { newUuid } from '../lib/new-uuid.ts'
import { ONE_UNIT, parseScanInput } from '../lib/pos-input.ts'
import { priceAt } from '../lib/pos-pricing.ts'
import { REASON_LABELS, formatUnitPrice } from '../lib/price-format.ts'
import { readRememberedPaper, rememberPaper } from '../lib/remembered-paper.ts'
import {
  readRememberedTerminal,
  rememberTerminal,
} from '../lib/remembered-terminal.ts'
import { useScanOwner } from '../lib/scan-owner.ts'
import { useDebounced } from '../lib/use-debounced.ts'
import { HEALTH_QUERY_KEY, useTillBackend } from '../lib/use-till-backend.ts'

/**
 * O PDV (Master Specification section 18).
 *
 * Section 18 does not ask for a form. It asks for teclado, velocidade, barcode
 * scanner, pesquisa rápida, atalhos and baixa latência, and every decision on
 * this screen comes from one of those six words:
 *
 *   * **One field has the focus, always.** A scanner is a keyboard that types a
 *     barcode and presses Enter, so the till's resting state is a focused input
 *     that answers Enter by adding an item — never by submitting a form.
 *
 *   * **One round trip per scan, and none for anything else.** `/pos/lookup`
 *     returns the product AND its price tiers together, so pressing `+` on a
 *     line, re-scanning something already in the cart, or reading the total
 *     costs no network at all. The re-pricing runs `resolvePrice` from
 *     `@aeris/pricing` and the total runs `computeTotals` from `@aeris/sales` —
 *     the same functions the server runs, not copies of them.
 *
 *   * **The shortcuts are on the screen.** A shortcut nobody can discover is a
 *     shortcut nobody uses; the footer lists them and F1 opens the full list.
 *
 * What this screen is NOT is a second sales domain. Finishing a sale is one
 * POST that writes a `sales_document`, consumes stock, records the payments and
 * puts the cash in the drawer inside ONE transaction (ADR-0017). If it fails,
 * nothing happened.
 *
 * Nothing here does arithmetic on a `number` and nothing formats with `Intl`,
 * which takes one. ADR-0016.
 */

/** A line in the cart. Everything on it has already been captured. */
type PosLine = {
  readonly key: string
  readonly variantId: string
  readonly description: string
  readonly sku: string
  /** O código do sistema antigo, que é como o balcão chama a peça. */
  readonly externalCode: string | null
  /** A miniatura da foto principal, quando o produto tem uma. */
  readonly thumbUrl: string | null
  /**
   * O saldo na filial quando o item entrou (F132), para a linha dizer quanto
   * tem. Nulo quando o produto não conta estoque.
   */
  readonly onHand: string | null
  readonly unit: string
  readonly quantity: Decimal
  readonly unitPrice: Decimal
  readonly priceListId: string | null
  readonly priceBasis: 'base-price' | 'quantity-tier' | 'manual'
  /**
   * The tiers this line was priced from.
   *
   * Kept per line and thrown away with it. A till that cached tiers across
   * sales would sell at yesterday's prices, and nobody would find out until a
   * customer complained.
   */
  readonly snapshot: PosPricingSnapshot
}

const SHORTCUTS: readonly (readonly [string, string])[] = [
  ['Enter', 'Adiciona o item digitado ou lido'],
  ['3*código', 'Informa a quantidade antes do código'],
  ['↑ ↓', 'Escolhe o item da lista, ou seleciona uma linha da venda'],
  ['+ / −', 'Aumenta ou diminui a quantidade da linha selecionada'],
  ['F2', 'Recebimento e conclusão da venda'],
  ['F3', 'Volta o cursor para o campo de leitura'],
  ['F4', 'Cliente da venda'],
  ['F9', 'Salva o orçamento e segura a mercadoria'],
  ['F10', 'Despacha a entrega com o motoboy, para receber depois'],
  ['F6', 'Remove a linha selecionada'],
  ['F7', 'Digita a quantidade exata da linha selecionada'],
  ['F8', 'Altera o preço da linha selecionada, com permissão'],
  ['Alt+1…9', 'Escolhe a forma de pagamento, na tela de recebimento'],
  ['F1 ou ?', 'Mostra esta ajuda'],
  ['Esc', 'Fecha a lista, ou cancela a venda em andamento'],
]

/** `2` rather than `2.000000`, for a number a person reads at a glance. */
const asQuantity = (value: string): string =>
  toString(parseHumanAmount(value, QUANTITY_SCALE) ?? ZERO_MONEY).replace(
    /\.?0+$/,
    ''
  )

/**
 * Whether adding this would be refused, and can be said BEFORE it is added.
 *
 * F028a: the refusal used to arrive at the payment step, after the operator
 * built the whole cart. A product the shop does not count can never be out of
 * stock, which is the other half of F028.
 *
 * E uma loja que autorizou vender abaixo de zero nunca fica sem estoque (F103):
 * o saldo passa a negativo e aparece em Estoque → A acertar. São três respostas
 * diferentes para "posso vender isto?", e cada uma tem um dono: o produto, a
 * empresa, e a empresa de novo.
 */
const outOfStock = (match: PosMatch, allowNegative: boolean): boolean =>
  !allowNegative &&
  match.tracksStock &&
  compare(
    parseHumanAmount(match.onHand ?? '0', QUANTITY_SCALE) ?? ZERO_MONEY,
    zero(QUANTITY_SCALE)
  ) <= 0

/** O vendedor escolhido fica no terminal, como o próprio terminal já fica. */
const SALESPERSON_KEY = 'aeris.pos-salesperson'

/**
 * Quantos produtos a busca do balcão traz (F211). Eram 8 — "note 8" escondia
 * metade dos Note 8, e não havia como rolar até eles.
 */
const POS_LIST_LIMIT = 50

let lineSequence = 0

export const PdvPage = (): ReactNode => {
  const { allows, hasFeature, session: authSession } = useAuth()
  const classic = useBalcaoAnterior()
  const queryClient = useQueryClient()

  /*
   * Contra quem esta tela está vendendo (F042).
   *
   * `mode` decide de onde vêm os dados; `capabilities` decide o que a tela
   * oferece. Nunca `if (mode === 'edge')` espalhado: em três features isso
   * vira quinze condicionais e duas versões que divergem — o que a §26 do
   * Master Spec proíbe.
   */
  const till = useTillBackend()
  const onEdge = till.mode === 'edge'

  const branchId = authSession?.context?.branchId ?? undefined
  const canCancel = allows('sales.cancel')

  /**
   * Se esta pessoa recebe dinheiro (F035, ADR-0027).
   *
   * O balcão tem dois lugares. O vendedor monta o carrinho e imprime o
   * orçamento — o papel que o cliente atravessa a loja carregando — e nunca
   * abre uma gaveta; o caixa recebe. É a MESMA tela: duplicá-la seria criar
   * duas cópias que divergem, e a §26 da Master Spec proíbe isso pelo mesmo
   * motivo entre Site Mode e PC Mode.
   *
   * A mesma costura que `/orcamentos` já usa. Tudo o que depende de caixa
   * aberto passa a depender DISTO primeiro: exigir turno aberto de quem não
   * pode abrir turno nenhum é uma tela que nunca abre.
   */
  const canTakeMoney = allows('pos.operate') && allows('payment.write')

  const [terminalId, setTerminalId] = useState(readRememberedTerminal)
  const hasTerminal = terminalId.trim().length > 0
  const [openingFloat, setOpeningFloat] = useState('')

  const [lines, setLines] = useState<PosLine[]>([])
  const [selected, setSelected] = useState(0)
  const [term, setTerm] = useState('')
  const [matches, setMatches] = useState<readonly PosMatch[]>([])
  const [highlighted, setHighlighted] = useState(0)
  /** O que acabou de entrar na venda, para quem bipou ver que foi (F212). */
  const [added, setAdded] = useState<string | null>(null)
  /**
   * A ordem das buscas (F212): uma resposta atrasada não troca a lista nem o
   * aviso de uma busca mais nova. O item bipado entra de qualquer jeito.
   */
  const lookupSequence = useRef(0)
  /**
   * How many of the next item, typed instead of prefixed.
   *
   * `3*código` still works and is faster for somebody who knows it — it stays
   * in the hint and in the shortcut list. This is for everybody else: a field
   * beside the search, which is what a person looks for when they have four of
   * something in their hand.
   *
   * Reset to one after every item, because the next customer's next product is
   * almost always a single unit and a sticky quantity is how three get sold as
   * thirty.
   */
  const [nextQuantity, setNextQuantity] = useState('1')
  const [customer, setCustomer] = useState<{
    id: string
    name: string
  } | null>(null)

  const [choosingCustomer, setChoosingCustomer] = useState(false)
  /**
   * A venda esperando a troca (F365). O cliente chega com a peça para trocar
   * e leva outra: o caixa abre a troca daqui, sem largar o carrinho. Concluída,
   * o vale dela entra no saldo do cliente, que já é o desta venda. Pedida (o
   * vendedor não conclui troca), a venda espera o caixa aprovar.
   */
  const [exchanging, setExchanging] = useState(false)
  const [awaitingExchange, setAwaitingExchange] = useState<{
    readonly number: number | null
    readonly status: string
    readonly voucher: string
  } | null>(null)
  /** O orçamento recém-gravado, aberto para impressão. F034. */
  const [quote, setQuote] = useState<SalesDocumentDetail | null>(null)
  /**
   * O diálogo do entregador, aberto sobre o carrinho (F101).
   *
   * A pergunta é uma só — quem leva — porque o resto não é escolha de ninguém
   * no balcão: a mercadoria sai, o título nasce, e o dinheiro chega quando o
   * motoboy voltar.
   */
  const [delivering, setDelivering] = useState(false)
  const [paying, setPaying] = useState(false)
  const [helping, setHelping] = useState(false)
  /** The index whose quantity is being typed, and what has been typed so far. */
  const [editingQuantity, setEditingQuantity] = useState<number | null>(null)
  const [quantityDraft, setQuantityDraft] = useState('')
  /**
   * The same, for the price.
   *
   * Section 18's counter is a keyboard, so this is F8 beside F7 rather than a
   * dialog: the manager who drops a price is standing at the till with a
   * customer waiting, and a modal is three interactions where one will do.
   */
  const [editingPrice, setEditingPrice] = useState<number | null>(null)
  const [priceDraft, setPriceDraft] = useState('')
  /**
   * Whether this operator may sell for something the table did not produce.
   *
   * Read here to decide what the screen OFFERS. The rule itself is on the
   * server, which re-prices every line and refuses a departure from a role
   * without this — a browser check is a courtesy to the honest, and section 28
   * says the honest are not who a rule is for.
   */
  const canOverridePrice = allows('sales.price.override')
  /**
   * Esc asks before it wipes.
   *
   * It used to clear the cart outright and say so in a line of text. A cart is
   * minutes of a customer's time and Esc is one key away from every other key;
   * losing twenty scanned items to a mistyped shortcut is not something a
   * notice afterwards repairs.
   */
  const [confirmingCancel, setConfirmingCancel] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  /*
   * O que DEU CERTO fica separado do que deu errado. Um aviso só, pintado de
   * vermelho, anunciava "Pedido despachado" e "Venda cancelada" com cara de
   * erro — e quem vê vermelho no caixa para tudo para conferir.
   */
  const [success, setSuccess] = useState<string | null>(null)
  const [confirmingCompletedCancel, setConfirmingCompletedCancel] =
    useState(false)
  /**
   * A venda que acabou de sair — e de qual dos dois balcões (F042).
   *
   * Discriminado em vez de um `PosCheckoutResponse` com campos vazios: a venda
   * do Edge **não tem** número de documento nem detalhe imprimível, e fingir
   * que tem produziria um comprovante com "Nº —" que o cliente guarda achando
   * que é a nota. O que ela tem é uma referência local, e é isso que a tela
   * mostra, dizendo o que ela é.
   */
  const [finished, setFinished] = useState<
    | { readonly kind: 'cloud'; readonly response: PosCheckoutResponse }
    | {
        readonly kind: 'edge'
        readonly reference: string
        readonly total: string
        readonly changeDue: string
        /** O papel da venda offline, sem número até subir (F197). */
        readonly receipt: ProvisionalReceipt
      }
    | null
  >(null)
  /**
   * The receipt, and the paper it is laid out for.
   *
   * The width is remembered per device, like the till and the sidebar: it is a
   * property of the printer standing at this counter, and a shop with a 58mm
   * roll at the till and 80mm in the office wants both answers.
   */
  const [printing, setPrinting] = useState(false)
  const [paper, setPaper] = useState<PaperWidth>(readRememberedPaper)

  const scanRef = useRef<HTMLInputElement>(null)
  const newSaleRef = useRef<HTMLButtonElement>(null)

  // Shared with /caixa: the till belongs to the counter, not to the user. A
  // memory of the last choice only — TerminalSelect opens empty if what is
  // stored here is no longer in the register.
  useEffect(() => {
    rememberTerminal(terminalId)
  }, [terminalId])

  /**
   * Hand focus to "Nova venda" the moment a receipt appears.
   *
   * The till is keyboard-driven everywhere else; without this it would ask for
   * the mouse exactly once per customer, which over a shift is the difference
   * between a fast counter and a slow one.
   */
  useEffect(() => {
    if (finished !== null) newSaleRef.current?.focus()
  }, [finished])

  // -------------------------------------------------------------------------
  // What the till needs before it can sell
  // -------------------------------------------------------------------------

  const current = useQuery({
    queryKey: ['cash-session', { terminalId, branchId, onEdge }],
    queryFn: async ({ signal }) => {
      /*
       * No modo Edge o turno vem do Edge — e é o MESMO turno, porque o
       * snapshot desce o caixa que a nuvem já tinha aberto e o Edge adota o id
       * dela (F042). A gaveta é uma só; o que muda é quem responde agora.
       */
      if (onEdge) {
        const local = await edgeCurrentSession(
          normalizeTerminalId(terminalId),
          signal
        )

        if (local === null) return { session: null }

        /*
         * Dito na forma da nuvem, para a tela falar um vocabulário só.
         *
         * O que o Edge não sabe vem do contexto de quem está logado — a filial
         * é a mesma — ou é null, que é a resposta honesta. `openedByName` é
         * null porque o Edge não guarda nomes de gente: ele guarda um id, e
         * inventar um nome aqui seria escrever no comprovante quem talvez não
         * tenha aberto a gaveta.
         */
        return {
          session: {
            id: local.id,
            branchId: authSession?.context?.branchId ?? '',
            branchName: authSession?.context?.branchName ?? '',
            terminalId: local.terminalId,
            status: local.status,
            openedByUserId: '',
            openedByName: null,
            openedAt: local.openedAt,
            openingFloat: local.openingFloat,
            closedAt: null,
            closedByName: null,
            countedAmount: null,
            expected: local.expectedAmount,
            difference: null,
            notes: null,
            version: 0,
          },
        }
      }

      return await fetchCurrentCashSession(
        {
          terminalId: normalizeTerminalId(terminalId),
          ...(branchId ? { branchId } : {}),
        },
        signal
      )
    },
    // Nothing to ask about until a till is chosen — e nada a perguntar a quem
    // não opera caixa, que só receberia 403 de uma pergunta que não é dele.
    enabled: hasTerminal && canTakeMoney,
  })

  const session = current.data?.session ?? null

  const methods = useQuery({
    queryKey: ['payment-methods', 'active', onEdge ? 'edge' : 'cloud'],
    /*
     * Sem internet, as formas vêm do Edge (F197). Antes vinham sempre da
     * nuvem: o caixa aberto já sem internet não tinha forma nenhuma e não
     * conseguia receber.
     */
    queryFn: async ({ signal }) =>
      onEdge
        ? {
            methods: (await edgePaymentMethods(signal)).map(
              toPaymentMethodSummary
            ),
          }
        : await fetchPaymentMethods({ status: 'active' }, signal),
    // Só quem recebe precisa saber como se recebe.
    enabled: canTakeMoney,
  })

  /**
   * The identified customer's credit.
   *
   * Fetched as soon as a customer is chosen rather than when the payment dialog
   * opens, so the figure is on screen the instant the operator reaches it —
   * asking at dialog-open time would put a spinner in the middle of a sale.
   * Only when a customer is identified: Consumidor Final has no credit and
   * never will.
   */
  const credit = useQuery({
    queryKey: ['credit', customer?.id],
    queryFn: ({ signal }) => fetchCredit(customer?.id ?? '', signal),
    // `finance.read` é do caixa e do gerente, não do vendedor: sem a condição,
    // a tela do vendedor abriria com um 403 no console a cada cliente escolhido.
    enabled: customer !== null && allows('finance.read'),
  })

  /*
   * O saldo de vale do cliente (F126), lido junto com o crédito e pelo mesmo
   * motivo: o número tem de estar na tela quando o operador escolhe "Vale", e
   * não depois de um spinner no meio da venda. `exchange.read` é do caixa e do
   * vendedor — os dois atendem quem volta com vale.
   */
  const voucher = useQuery({
    queryKey: ['customer-credit', customer?.id],
    queryFn: ({ signal }) => fetchCustomerCredit(customer?.id ?? '', signal),
    enabled: customer !== null && allows('exchange.read'),
  })

  /**
   * Quem vendeu, que nem sempre é quem digitou (F034).
   *
   * A Elite Digital tem mais vendedores no balcão que logins abertos: um
   * atende, outro opera o caixa. Gravar sempre o operador apaga o vendedor de
   * toda a história de vendas — e é essa história que responde quem atendeu o
   * cliente que voltou reclamando.
   *
   * Lembrado no terminal, como o próprio terminal já é: quem trabalha num
   * caixa a manhã inteira não escolhe o próprio nome a cada venda.
   */
  const salespeople = useQuery({
    queryKey: ['salespeople'],
    queryFn: ({ signal }) => fetchSalespeople(signal),
  })

  /**
   * Quem vendeu. Sempre alguém, e nunca por omissão (F035).
   *
   * O padrão é quem está logado, porque na maior parte das lojas quem digita
   * também vendeu. O que sumiu foi a opção vazia: "não informado" virava o
   * operador em silêncio, e o vendedor desaparecia da história da venda sem
   * ninguém ter decidido isso.
   */
  /*
   * Outra pessoa como vendedora só com a senha DELA (F298) — o dono, 20/10:
   * vale para todo mundo, caixa e gerente inclusive. A confirmação fica na
   * memória da tela até recarregar; o terminal não lembra mais um nome que
   * ninguém confirmou (o `localStorage` de antes guardava qualquer escolha).
   */
  const [salespersonUserId, setSalespersonUserId] = useState<string>(() => {
    window.localStorage.removeItem(SALESPERSON_KEY)
    return authSession?.user.id ?? ''
  })
  const [confirmedSellers, setConfirmedSellers] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  const [confirmingSeller, setConfirmingSeller] = useState<{
    readonly userId: string
    readonly displayName: string
  } | null>(null)

  const chooseSalesperson = (userId: string): void => {
    if (userId === authSession?.user.id || confirmedSellers.has(userId)) {
      setSalespersonUserId(userId)
      return
    }
    const person = salespeople.data?.items.find(
      (item) => item.userId === userId
    )
    setConfirmingSeller({
      userId,
      displayName: person?.displayName ?? 'quem vendeu',
    })
  }

  /**
   * Um vendedor lembrado que não trabalha aqui volta a ser quem está logado.
   *
   * O nome fica no terminal, e o terminal atravessa logins e contas: entrar com
   * outro usuário — ou noutra empresa — deixava no alto da tela o nome de
   * alguém que aquela loja não conhece, e o `select` caía calado no primeiro da
   * lista.
   *
   * Derivado durante a renderização, e não corrigido num efeito. O estado
   * guardado continua sendo o que a pessoa escolheu; o que muda é o que a tela
   * usa enquanto aquela escolha não vale aqui — e escrever por cima num efeito
   * apagaria a escolha só porque a lista ainda não tinha chegado.
   */
  const knownSalesperson =
    salespeople.data === undefined ||
    salespeople.data.items.some((person) => person.userId === salespersonUserId)

  const chosenSalesperson = knownSalesperson
    ? salespersonUserId
    : (authSession?.user.id ?? '')

  /**
   * O vendedor que vai na venda: o escolhido no balcão (F298). Para todo
   * mundo — a vendedora num computador com outro login também escolhe o
   * próprio nome, com a senha dela.
   */
  const sellingUserId = chosenSalesperson

  const priceLists = useQuery({
    queryKey: ['price-lists'],
    queryFn: ({ signal }) => fetchPriceLists(signal),
  })

  /**
   * The customer defaults to the tenant's Consumidor Final.
   *
   * The customer is never null (F013, decision 1) and a walk-in who did not
   * identify themselves is the ordinary case at a counter, so it is a default
   * rather than a field somebody has to fill before the first scan.
   *
   * **Pedido, e não procurado (F104).** Isto era `fetchParties({ pageSize: 50
   * })` seguido de `find(isSystem) ?? items[0]`: numa conta com 8988 pessoas o
   * Consumidor Final não está entre os cinquenta primeiros por nome, o `find`
   * falhava, e o balcão passava o dia vendendo no nome de quem ordena primeiro
   * — um cadastro que ninguém escolheu, com o extrato e a ficha dele.
   *
   * `pageSize: 1` porque a resposta é uma linha: o índice único parcial em
   * `is_system` garante uma por conta.
   */
  const consumers = useQuery({
    queryKey: ['parties', { role: 'customer', system: true }],
    queryFn: ({ signal }) =>
      fetchParties(
        { role: 'customer', status: 'active', system: true, pageSize: 1 },
        signal
      ),
  })

  /*
   * Sem fallback, deliberadamente.
   *
   * O `?? items[0]` que estava aqui é o mesmo que a compra já matou uma vez
   * (`purchase-form.tsx`): *"um default errado é pior que default nenhum: ele
   * produz um documento completo e plausível que ninguém foi convidado a
   * confirmar"*. Sem Consumidor Final não há padrão, e a tela diz isso em vez
   * de nomear um estranho.
   */
  const defaultCustomer = useMemo(() => {
    const system = consumers.data?.items[0]
    return system === undefined
      ? null
      : { id: system.id, name: system.tradeName ?? system.legalName }
  }, [consumers.data])

  const effectiveCustomer = customer ?? defaultCustomer

  /**
   * A conta não tem Consumidor Final, e por isso não há venda anônima (F104).
   *
   * Uma falha de provisionamento, não uma escolha de quem está no balcão — mas
   * ela chega ao balcão, então é aqui que precisa estar escrita. Vender segue
   * possível escolhendo o cliente; o que para é vender sem saber para quem.
   *
   * Só depois que a consulta respondeu: enquanto ela carrega, `defaultCustomer`
   * é nulo sem que isso queira dizer nada, e uma frase que aparece e some é
   * pior que nenhuma.
   */
  const noDefaultCustomer =
    consumers.isSuccess && defaultCustomer === null && customer === null
      ? 'Esta conta não tem Consumidor Final cadastrado. Escolha o cliente (F4) para vender.'
      : null

  /**
   * Se dá para despachar uma entrega agora, e a razão quando não dá (F101).
   *
   * Três condições, e cada uma é uma frase diferente na tela: quem não opera o
   * caixa não vê o botão (ADR-0027, como em Finalizar); sem nuvem não há
   * numeração nem financeiro; e o Consumidor Final não pode dever — a saída
   * levanta um título a receber, e uma dívida precisa de alguém que a deva.
   *
   * O servidor recusa as três de qualquer jeito. Dizer aqui é para que o
   * operador saiba ANTES de montar o carrinho inteiro.
   */
  /**
   * Se a loja autorizou vender abaixo de zero (F103).
   *
   * Duas fontes porque são dois balcões: na nuvem a tela lê a empresa; no
   * balcão offline (Edge) a resposta desce no snapshot e chega pelo status do
   * Edge. A mesma
   * política, e por isso a MESMA `outOfStock` decide nos dois — duplicar a
   * regra por modo é o que a §26 do Master Spec proíbe.
   *
   * Enquanto a resposta não chega, `false`: recusar é o lado conservador, e é
   * o comportamento de sempre.
   */
  const companyId = authSession?.context?.companyId ?? null
  const company = useQuery({
    queryKey: ['company', companyId],
    queryFn: ({ signal }) => fetchCompany(companyId ?? '', signal),
    // Mesma chave de Produtos e do menu: o cache é um só, e isto não custa
    // requisição nenhuma. No Edge não há nuvem a consultar.
    enabled: companyId !== null && !onEdge,
  })

  const allowNegative = onEdge
    ? (till.policy?.allowNegativeStock ?? false)
    : (company.data?.company.salesFlow.allowNegativeStock ?? false)

  const deliveryBlocked = !till.capabilities.delivery
    ? 'Sem internet, não dá para despachar: o pedido nasce numerado e levanta um título a receber.'
    : customer === null
      ? 'Uma entrega vira um título a receber, e uma dívida precisa de alguém que a deva. Escolha o cliente (F4).'
      : null

  const canDeliver = canTakeMoney && deliveryBlocked === null

  /**
   * A mesma razão, dita de dentro do recebimento (F105).
   *
   * O diálogo tapa a tela, então *"Escolha o cliente (F4)"* vira uma instrução
   * que não se cumpre de onde a pessoa está. O motivo é o mesmo; o caminho de
   * volta é que mudou, e é o caminho que a frase precisa dar.
   */
  const deliveryBlockedInDialog =
    deliveryBlocked !== null && till.capabilities.delivery && customer === null
      ? 'Uma entrega vira um título a receber, e uma dívida precisa de alguém que a deva. Volte (Esc) e escolha o cliente (F4).'
      : deliveryBlocked

  // -------------------------------------------------------------------------
  // The cart
  // -------------------------------------------------------------------------

  /**
   * The running total, from the same function the server stores its answer
   * from. Recomputed on every change rather than fetched: a total that lags the
   * cart is a total the operator stops trusting.
   */
  /*
   * O desconto da nota cheia — F100.
   *
   * O PDV não tinha nenhum: mandava `'0'` chumbado em três lugares. A loja
   * fecha desconto no balcão o tempo todo, e sem isto a caixa baixava o preço
   * item a item — o que some do relatório como "desconto" e vira "preço fora
   * da tabela", que é outra coisa e tem outra permissão.
   *
   * Percentual é o modo padrão porque é como a conversa acontece: "faz 10%
   * pra ele" é o que se diz, e o valor é a consequência.
   */
  const [discountMode, setDiscountMode] = useState<'amount' | 'percent'>(
    'percent'
  )
  const [discount, setDiscount] = useState('')

  const typedDiscount = useMemo(() => parseHumanAmount(discount), [discount])

  const headerDiscount = useMemo(
    () =>
      typedDiscount === null
        ? ZERO_MONEY
        : discountMode === 'percent'
          ? percentDiscount(typedDiscount)
          : amountDiscount(typedDiscount),
    [typedDiscount, discountMode]
  )

  const totals = useMemo(() => {
    const outcome = computeTotals(
      lines.map((line) => ({
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        discountAmount: ZERO_MONEY,
      })),
      headerDiscount
    )
    return outcome.ok ? outcome.totals : null
  }, [lines, headerDiscount])

  const total = totals?.total ?? ZERO_MONEY

  const focusScan = useCallback(() => {
    scanRef.current?.focus()
    scanRef.current?.select()
  }, [])

  const startNewSale = useCallback(() => {
    setLines([])
    setSelected(0)
    setTerm('')
    setMatches([])
    setCustomer(null)
    // O desconto é da venda, não do balcão: sem isto o "10%" do cliente
    // anterior ia para o próximo sem ninguém ver.
    setDiscount('')
    setNotice(null)
    setSuccess(null)
    setFinished(null)
    focusScan()
  }, [focusScan])

  /**
   * Prices a variant at a quantity and turns it into a line.
   *
   * Local, and therefore instant: the tiers came back with the scan. Returns
   * null and states why when nothing applies — never R$ 0,00, which is a
   * giveaway somebody will sell at.
   */
  const capture = useCallback(
    (
      match: PosMatch,
      snapshot: PosPricingSnapshot,
      quantity: Decimal
    ): PosLine | null => {
      const resolution = priceAt(snapshot, quantity)

      if (!resolution.applies) {
        setNotice(`${match.description}: ${REASON_LABELS[resolution.reason]}`)
        return null
      }

      lineSequence += 1

      return {
        key: `line-${String(lineSequence)}`,
        variantId: match.variantId,
        description: match.description,
        sku: match.sku,
        externalCode: match.externalCode,
        thumbUrl: match.thumbUrl,
        onHand: match.onHand ?? null,
        unit: match.unit,
        quantity,
        unitPrice: resolution.unitPrice,
        priceListId: resolution.list?.id ?? null,
        priceBasis: resolution.basis,
        snapshot,
      }
    },
    []
  )

  const addFromLookup = useCallback(
    (match: PosMatch, snapshot: PosPricingSnapshot, quantity: Decimal) => {
      setLines((currentLines) => {
        const existing = currentLines.findIndex(
          (line) => line.variantId === match.variantId
        )

        /**
         * Scanning the same thing twice adds to the line rather than making a
         * second one — and re-prices it, because the extra unit may have earned
         * a quantity tier. No round trip: the tiers are already here.
         */
        if (existing >= 0) {
          const line = currentLines[existing]
          if (line === undefined) return currentLines

          const merged = add(line.quantity, quantity)
          const repriced = priceAt(line.snapshot, merged)

          const next = [...currentLines]
          next[existing] = {
            ...line,
            quantity: merged,
            ...(repriced.applies
              ? { unitPrice: repriced.unitPrice, priceBasis: repriced.basis }
              : {}),
          }
          setSelected(existing)
          return next
        }

        const line = capture(match, snapshot, quantity)
        if (line === null) return currentLines

        setSelected(currentLines.length)
        return [...currentLines, line]
      })
    },
    [capture]
  )

  const lookup = useMutation({
    // Consulta, não gravação: não recarrega as outras telas (F212).
    meta: { readOnly: true },
    mutationFn: async (input: {
      term: string
      quantity: Decimal
      /** A cor escolhida na lista (F215); ausente no bipe e no Enter. */
      variantId?: string
    }) => {
      lookupSequence.current += 1
      const sequence = lookupSequence.current
      return {
        // No list named: a scan is priced by the account's default, and a line
        // that wants another one is moved after it exists. F026 took the
        // whole-sale table away, so there is nothing here to inherit.
        answer: onEdge
          ? await edgeLookup(input.term)
          : await posLookup({
              term: input.term,
              limit: POS_LIST_LIMIT,
              variantId: input.variantId,
            }),
        quantity: input.quantity,
        term: input.term,
        chosen: input.variantId !== undefined,
        sequence,
      } as const
    },
    onSuccess: ({ answer, quantity, term: searched, chosen, sequence }) => {
      const latest = sequence === lookupSequence.current
      if (latest) setNotice(null)

      const only = answer.matches[0]

      if (answer.matches.length === 0) {
        if (!latest) return
        setMatches([])
        setAdded(null)
        setNotice(
          `Nenhum produto com o código, código de barras ou nome “${searched}”.`
        )
        return
      }

      if (answer.matches.length === 1 && only !== undefined) {
        if (answer.pricing === null) {
          setNotice(`${only.description}: sem preço na tabela escolhida.`)
          return
        }
        setMatches([])
        /*
         * O bipe passa pela mesma conferência da escolha na lista (F132). Só o
         * item escolhido era recusado sem estoque; o bipado entrava e a venda
         * só descobria no F2, com o cliente esperando.
         */
        if (outOfStock(only, allowNegative)) {
          setNotice(`${only.description}: sem estoque nesta filial.`)
          return
        }
        if (latest) {
          setTerm('')
          setNextQuantity('1')
        }
        addFromLookup(only, answer.pricing, quantity)
        setAdded(only.description)
        return
      }

      if (!latest) return

      /*
       * A escolha que volta ambígua não vira a mesma lista de novo (F215).
       *
       * Foi o que a PRETO fez em 30/09: escolhida, voltava a lista com as três
       * cores, o destaque na primeira, e nada dizia que não tinha entrado.
       */
      if (chosen) {
        setMatches([])
        setAdded(null)
        setNotice(
          `Não consegui adicionar “${searched}”. Procure de novo e escolha outra vez.`
        )
        return
      }

      // Several. The operator chooses; only then is the price fetched, so a
      // fragment does not price eight products nobody asked about.
      setMatches(answer.matches)
      setHighlighted(0)
      /*
       * Um código que não é de ninguém, mas é pedaço de vários (F212): a lista
       * aparece, e o aviso diz que nenhum deles é o código digitado — antes
       * vinham oito produtos sem explicação e o balcão achava que "não foi".
       */
      if (
        /^\d{4,}$/.test(searched) &&
        !answer.matches.some((match) => match.exact)
      ) {
        setNotice(
          `Nenhum produto tem o código “${searched}”. Estes têm “${searched}” no código ou no nome.`
        )
      }
    },
    onError: (error: unknown) => {
      setAdded(null)
      setNotice(
        // Um 500 nunca chega ao balcão como "Erro interno." (F212).
        error instanceof ApiError && error.status < 500
          ? error.message
          : 'A busca falhou. Pressione Enter para tentar de novo.'
      )
    },
  })

  /** The field, unless the term carried its own `3*` prefix, which wins. */
  const quantityFor = useCallback(
    (prefixed: Decimal): Decimal => {
      if (compare(prefixed, ONE_UNIT) !== 0) return prefixed
      return parseHumanAmount(nextQuantity, QUANTITY_SCALE) ?? ONE_UNIT
    },
    [nextQuantity]
  )

  const submitTerm = useCallback(() => {
    const parsed = parseScanInput(term)
    if (parsed === null) return
    lookup.mutate({
      term: parsed.code,
      quantity: quantityFor(parsed.quantity),
    })
  }, [term, lookup, quantityFor])

  /*
   * O bipe com o foco fora do campo (F206): um clique no carrinho tirava o foco
   * e o próximo bipe se perdia. Agora ele entra como se tivesse caído no campo.
   */
  useScanOwner((code) => {
    const parsed = parseScanInput(code)
    if (parsed === null) return
    lookup.mutate({
      term: parsed.code,
      quantity: quantityFor(parsed.quantity),
    })
  })

  /**
   * What the operator is typing, answered while they type it.
   *
   * The endpoint already returned candidates; the till only asked after Enter,
   * so somebody who did not know a code by heart had to press a key to find
   * out whether the shop even sells the thing. Now the list is simply there.
   *
   * ## Why this does not break the scanner
   *
   * A scanner is a keyboard that types a barcode in a few milliseconds and
   * presses Enter. The debounce is 150ms, so at the instant Enter arrives no
   * suggestion query has fired and `submitTerm` takes the exact path it always
   * took — one request, priced, added. Typing by hand is the slow case, and
   * the slow case is the one that wants help.
   */
  const typed = useDebounced(term, 150)

  const suggestions = useQuery({
    queryKey: ['pos-suggest', typed, onEdge],
    queryFn: async ({ signal }) => {
      const term = parseScanInput(typed)?.code ?? typed
      return onEdge
        ? await edgeLookup(term, signal)
        : await posLookup({ term, limit: POS_LIST_LIMIT }, signal)
    },
    // Uma busca que falhou não se repete sozinha: a próxima tecla já é
    // outra busca (F212).
    retry: false,
    // Two characters, because one letter matches most of a catalogue and
    // answering that is work nobody reads.
    enabled:
      finished === null &&
      matches.length === 0 &&
      (parseScanInput(typed)?.code.length ?? 0) >= 2,
  })

  /**
   * The list under the field: what was searched, or what is being typed.
   *
   * `matches` wins because it is an ANSWER — the operator pressed Enter on
   * something ambiguous and is choosing. Suggestions are a guess about a term
   * still being typed, and replacing a chosen shortlist with a guess is how a
   * list moves under somebody's finger.
   */
  const visible: readonly PosMatch[] =
    matches.length > 0 ? matches : (suggestions.data?.matches ?? [])

  /*
   * A list that arrives shorter than the last one must not leave the highlight
   * pointing past its end, where Enter would add nothing and look broken.
   *
   * Clamped on read rather than corrected in an effect: the highlight is a
   * function of the list, and storing a value that has to be repaired after
   * every render is how a render loop starts.
   */
  const highlight = Math.min(highlighted, Math.max(visible.length - 1, 0))

  /*
   * F5 com itens na venda pergunta antes (F212).
   *
   * O carrinho mora só na memória desta tela. Em 29/09 cada F5 jogou fora o
   * que estava bipado, e duas peças nunca chegaram a documento nenhum.
   */
  const hasLines = lines.length > 0
  useEffect(() => {
    if (!hasLines) return
    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault()
    }
    window.addEventListener('beforeunload', warn)
    return () => {
      window.removeEventListener('beforeunload', warn)
    }
  }, [hasLines])

  const typedCode = parseScanInput(term)?.code ?? term.trim()
  /**
   * A lista na tela é do que está no campo? (F212)
   *
   * A respondida (`matches`) é apagada a cada tecla, então é sempre atual. A
   * sugerida é do termo que a espera de 150 ms deixou passar.
   */
  const listIsCurrent =
    matches.length > 0 || (parseScanInput(typed)?.code ?? typed) === typedCode

  /**
   * Uma linha sob o campo que diz o que está acontecendo (F212).
   *
   * O balcão de 29/09 deu F5 quatro vezes porque nada na tela dizia se a
   * busca estava indo, se falhou, ou se não achou.
   */
  const searchStatus: string | null = lookup.isPending
    ? 'Procurando…'
    : notice !== null || matches.length > 0 || typedCode.length < 2
      ? added === null
        ? null
        : `Adicionado: ${added}`
      : suggestions.isFetching && visible.length === 0
        ? 'Procurando…'
        : suggestions.isError && listIsCurrent
          ? 'A busca falhou. Pressione Enter para tentar de novo.'
          : suggestions.isSuccess && listIsCurrent && visible.length === 0
            ? `Nenhum produto com “${typedCode}”. Enter procura de novo.`
            : null

  const chooseMatch = useCallback(
    (match: PosMatch) => {
      /*
       * Refused HERE, not at the payment step.
       *
       * The old order was: build the whole cart, press F2, and read "estoque
       * insuficiente" on the receiving screen — with a customer waiting and no
       * clue which of eight items was the problem. The lookup now carries the
       * balance, so the answer is available at the moment it is useful.
       */
      if (outOfStock(match, allowNegative)) {
        setNotice(`${match.description}: sem estoque nesta filial.`)
        return
      }

      // No Edge a lista nunca traz preço — ele vem na consulta pelo código,
      // logo abaixo (F212). Recusar aqui era não deixar escolher nada offline.
      if (match.unitPrice === null && !onEdge) {
        setNotice(`${match.description}: sem preço na tabela.`)
        return
      }

      setMatches([])
      setTerm('')
      // The exact SKU, so the second lookup is the unambiguous path and comes
      // back with the price — e a cor dita, para o código do produto não
      // trazer as irmãs junto (F215).
      lookup.mutate({
        term: match.sku,
        quantity: quantityFor(ONE_UNIT),
        ...(onEdge ? {} : { variantId: match.variantId }),
      })
    },
    [lookup, quantityFor, allowNegative, onEdge]
  )

  const changeQuantity = useCallback((index: number, delta: Decimal) => {
    setLines((currentLines) => {
      const line = currentLines[index]
      if (line === undefined) return currentLines

      const next = add(line.quantity, delta)
      // Down to zero removes the line: pressing `−` on a single unit means the
      // customer changed their mind, and leaving a line of zero would sell it.
      if (compare(next, zero(QUANTITY_SCALE)) <= 0) {
        return currentLines.filter((_, position) => position !== index)
      }

      const repriced = priceAt(line.snapshot, next)
      const updated = [...currentLines]
      updated[index] = {
        ...line,
        quantity: next,
        // A hand-set price survives stepping the quantity, for the reason
        // spelled out in `setQuantity`.
        ...(repriced.applies && line.priceBasis !== 'manual'
          ? { unitPrice: repriced.unitPrice, priceBasis: repriced.basis }
          : {}),
      }
      return updated
    })
  }, [])

  /**
   * Sets a line to an exact quantity, rather than stepping to it.
   *
   * `+` is fine for the second unit and absurd for the twelfth. Twelve is also
   * where a quantity tier usually starts, so the slow path was the one that
   * changed the price.
   */
  const setQuantity = useCallback((index: number, quantity: Decimal) => {
    setLines((currentLines) => {
      const line = currentLines[index]
      if (line === undefined) return currentLines
      if (compare(quantity, zero(QUANTITY_SCALE)) <= 0) {
        return currentLines.filter((_, position) => position !== index)
      }

      const repriced = priceAt(line.snapshot, quantity)
      const updated = [...currentLines]
      updated[index] = {
        ...line,
        quantity,
        /*
         * A hand-set price survives a change of quantity.
         *
         * Re-pricing here would quietly undo the decision somebody just made:
         * they typed R$ 8,00, pressed `+`, and the line went back to R$ 10,00
         * with nothing said. Whoever wants the table's price again can clear
         * the override, which is an act.
         */
        ...(repriced.applies && line.priceBasis !== 'manual'
          ? { unitPrice: repriced.unitPrice, priceBasis: repriced.basis }
          : {}),
      }
      return updated
    })
  }, [])

  /**
   * A price the operator decided, on one line.
   *
   * Marked `manual`, which is what the server will independently conclude when
   * it re-prices — the two agree because they are looking at the same table,
   * not because the till told the server what to think.
   */
  const setUnitPrice = useCallback((index: number, unitPrice: Decimal) => {
    setLines((currentLines) => {
      const line = currentLines[index]
      if (line === undefined) return currentLines

      const updated = [...currentLines]
      updated[index] = { ...line, unitPrice, priceBasis: 'manual' }
      return updated
    })
  }, [])

  /**
   * Moves ONE line to another price list.
   *
   * The only thing on this screen that costs a round trip after the scan, and
   * it has to: the tiers a line carries are the tiers of the list it was
   * priced from, and another list is another set of prices nobody has read
   * yet. `/pos/lookup` by SKU is the unambiguous path and comes back with the
   * whole snapshot, so the line can be re-priced locally afterwards — the
   * quantity steps that follow cost nothing again.
   *
   * Refuses rather than guesses: a variant absent from the chosen list leaves
   * the line exactly as it was and says so. Falling back to the old price
   * while showing the new list's name would be a line that lies about where
   * its price came from.
   */
  const repriceLine = useMutation({
    meta: { readOnly: true },
    mutationFn: async (input: {
      index: number
      sku: string
      priceListId: string
    }) =>
      ({
        ...input,
        answer: await posLookup({
          term: input.sku,
          ...(input.priceListId.length > 0
            ? { priceListId: input.priceListId }
            : {}),
        }),
      }) as const,
    onSuccess: ({ index, answer }) => {
      const snapshot = answer.pricing

      if (snapshot === null) {
        setNotice('Não foi possível ler os preços dessa tabela.')
        return
      }

      setLines((currentLines) => {
        const line = currentLines[index]
        if (line === undefined) return currentLines

        const resolution = priceAt(snapshot, line.quantity)

        if (!resolution.applies) {
          setNotice(`${line.description}: ${REASON_LABELS[resolution.reason]}`)
          return currentLines
        }

        setNotice(null)

        const updated = [...currentLines]
        updated[index] = {
          ...line,
          snapshot,
          unitPrice: resolution.unitPrice,
          priceListId: resolution.list?.id ?? null,
          // Deliberately NOT preserved as manual: choosing a table is asking
          // for that table's price, which is the opposite of overriding it.
          priceBasis: resolution.basis,
        }
        return updated
      })
    },
    onError: (error: unknown) => {
      setNotice(
        error instanceof ApiError
          ? error.message
          : 'Não foi possível trocar a tabela de preço deste item.'
      )
    },
  })

  /** Back to whatever the price list says at this quantity. */
  const clearOverride = useCallback((index: number) => {
    setLines((currentLines) => {
      const line = currentLines[index]
      if (line === undefined) return currentLines

      const repriced = priceAt(line.snapshot, line.quantity)
      if (!repriced.applies) return currentLines

      const updated = [...currentLines]
      updated[index] = {
        ...line,
        unitPrice: repriced.unitPrice,
        priceBasis: repriced.basis,
      }
      return updated
    })
  }, [])

  const removeLine = useCallback((index: number) => {
    setLines((currentLines) =>
      currentLines.filter((_, position) => position !== index)
    )
    setSelected((current) => Math.max(current - 1, 0))
  }, [])

  // -------------------------------------------------------------------------
  // Finishing
  // -------------------------------------------------------------------------

  /**
   * Gravar o orçamento e segurar a mercadoria — uma chamada, uma transação.
   *
   * O diálogo de impressão abre em cima do resultado, e o carrinho é limpo:
   * aquele orçamento agora existe com número e com a peça reservada, e um
   * carrinho que continuasse na tela convidaria a lançar tudo de novo.
   */
  /*
   * A chave do orçamento deste carrinho (F250).
   *
   * Nasce no primeiro F9 e vale até o orçamento gravar ou o carrinho
   * esvaziar. Se a resposta do primeiro F9 se perde (rede, tempo, deploy), o
   * segundo manda a MESMA chave e o servidor devolve o orçamento que já
   * existe, em vez de criar o nº +1 — "salvou certinho, porém duplicou".
   */
  const quoteKey = useRef<string | null>(null)
  useEffect(() => {
    if (lines.length === 0) quoteKey.current = null
  }, [lines.length])

  const saveQuote = useMutation({
    mutationFn: async () =>
      await posQuote({
        id: (quoteKey.current ??= newUuid()),
        customerPartyId: effectiveCustomer?.id ?? '',
        salespersonUserId: sellingUserId,
        priceListId: null,
        /*
         * Um dos dois, nunca os dois — o servidor recusa `discount_named_twice`
         * justamente porque adivinhar qual vale seria escolher em silêncio o
         * desconto de uma venda.
         */
        ...(discountMode === 'percent'
          ? {
              discountAmount: '0',
              discountPercent:
                typedDiscount === null ? null : toString(typedDiscount),
            }
          : {
              discountAmount:
                typedDiscount === null ? '0' : toString(typedDiscount),
              discountPercent: null,
            }),
        lines: lines.map((line) => ({
          variantId: line.variantId,
          description: line.description,
          unit: line.unit,
          quantity: toString(line.quantity),
          unitPrice: toString(line.unitPrice),
          priceListId: line.priceListId,
          priceBasis: line.priceBasis,
          discountAmount: '0',
        })),
      }),
    onSuccess: (result) => {
      quoteKey.current = null
      /*
       * O servidor devolveu o orçamento que a primeira tentativa já tinha
       * gravado, com outro cliente: diz, em vez de deixar o vendedor achar
       * que trocou.
       */
      const asked = effectiveCustomer?.id ?? ''
      if (asked !== '' && result.document.customerPartyId !== asked) {
        setNotice(
          `O orçamento nº ${formatDocumentNumber(result.document.number, '')} já tinha sido gravado com outro cliente. Para trocar o cliente, use Editar em Orçamentos.`
        )
      }
      setQuote(result.document)
      setLines([])
      setCustomer(null)
      setDiscount('')
      setTerm('')
      setMatches([])
      void queryClient.invalidateQueries({ queryKey: ['stock'] })
    },
    /*
     * O F9 dizia o motivo de recusar? Não dizia nada (F248): sem `onError`, o
     * botão principal do vendedor voltava ao normal e o orçamento não existia
     * — e o carrinho continuava lá, parecendo salvo.
     */
    onError: (error: unknown) => {
      setNotice(
        error instanceof ApiError
          ? error.message
          : 'Não foi possível salvar o orçamento. O carrinho continua aqui: tente de novo.'
      )
    },
  })

  /**
   * A entrega que sai sem o dinheiro (F101).
   *
   * *"caixa cria pedido, motoboy faz a entrega e volta falando como que o
   * cliente pagou e ela anota dai"*. Uma chamada só: o pedido nasce, a
   * mercadoria sai do estoque e o título a receber é levantado na mesma
   * transação do servidor.
   *
   * Nenhum pagamento e nenhuma gaveta. O carrinho é limpo porque aquela venda
   * já saiu pela porta — deixá-lo na tela convidaria a lançar tudo de novo.
   */
  const deliver = useMutation({
    // O erro aparece na própria tela; o aviso geral não repete (F248).
    meta: { errorShownInline: true },
    // `null` é "Entregar na rota" (F266): o pedido espera a saída na fila.
    mutationFn: async (courierPartyId: string | null) =>
      await posOrder({
        customerPartyId: effectiveCustomer?.id ?? '',
        salespersonUserId: sellingUserId,
        courierPartyId,
        priceListId: null,
        // Um dos dois, nunca os dois — o servidor recusa `discount_named_twice`.
        ...(discountMode === 'percent'
          ? {
              discountAmount: '0',
              discountPercent:
                typedDiscount === null ? null : toString(typedDiscount),
            }
          : {
              discountAmount:
                typedDiscount === null ? '0' : toString(typedDiscount),
              discountPercent: null,
            }),
        notes: null,
        lines: lines.map((line) => ({
          variantId: line.variantId,
          description: line.description,
          unit: line.unit,
          quantity: toString(line.quantity),
          unitPrice: toString(line.unitPrice),
          priceListId: line.priceListId,
          priceBasis: line.priceBasis,
          discountAmount: '0',
        })),
      }),
    onSuccess: (result) => {
      setDelivering(false)
      setSuccess(
        result.document.courierPartyId === null
          ? `Pedido nº ${formatDocumentNumber(result.document.number, '')} na fila da rota. A mercadoria está separada; monte a saída em Rotas de entrega.`
          : `Pedido nº ${formatDocumentNumber(result.document.number, '')} despachado. O título está no Financeiro, e o acerto é feito na tela do motoboy.`
      )
      setLines([])
      setSelected(0)
      setCustomer(null)
      setDiscount('')
      setTerm('')
      setMatches([])
      void queryClient.invalidateQueries({ queryKey: ['stock'] })
      focusScan()
    },
  })

  /*
   * O atalho lê a mutação por referência.
   *
   * O handler de teclado é registrado uma vez, e pôr a mutação nas dependências
   * o recriaria a cada render — em cima de uma tela onde cada tecla conta. A
   * referência aponta sempre para a versão atual sem que o listener precise
   * saber disso.
   */
  const saveQuoteRef = useRef<() => void>(() => undefined)

  const quoting = saveQuote.isPending

  /*
   * A referência é atualizada num efeito, não durante a renderização.
   *
   * Escrever numa ref enquanto o componente renderiza é o que o React 19
   * proíbe: numa renderização descartada, a escrita já aconteceu e a ref passa
   * a apontar para um estado que nunca chegou à tela.
   */
  useEffect(() => {
    saveQuoteRef.current = () => {
      if (!quoting) saveQuote.mutate()
    }
  })

  const checkout = useMutation({
    mutationFn: async ({
      tenders,
      creditAuthorisationId,
    }: {
      tenders: readonly PosTender[]
      creditAuthorisationId: string | null
    }) => {
      if (onEdge) {
        /*
         * A mesma venda, contra o servidor da loja.
         *
         * As linhas vão com o preço capturado quando o item entrou, como na
         * nuvem — ADR-0017: o servidor não reprecifica. O que muda é quem
         * grava, e que esta venda entra numa fila em vez de virar documento
         * numerado agora.
         */
        const sale = await edgeCheckout({
          cashSessionId: session?.id ?? '',
          actorUserId: authSession?.user.id ?? null,
          salespersonUserId: sellingUserId === '' ? null : sellingUserId,
          lines: lines.map((line) => ({
            variantId: line.variantId,
            description: line.description,
            unit: line.unit,
            unitPrice: toString(line.unitPrice),
            quantity: toString(line.quantity),
            priceBasis: line.priceBasis,
            priceListId: line.priceListId,
          })),
          /*
           * O Edge recebe o desconto em REAIS, já derivado (F100).
           *
           * Ele aceita `discountAmount` desde sempre e não conhece percentual —
           * e não precisa: uma venda de balcão no Edge é fechada no mesmo
           * instante e nunca reaberta, então não há nada para lembrar. O valor
           * sai da MESMA `computeTotals` que pintou o total na tela, e por isso
           * não existe uma segunda implementação da regra do outro lado.
           */
          discountAmount: toString(totals?.discount ?? ZERO_MONEY),
          payments: tenders.map((tender) => ({
            paymentMethodId: tender.methodId,
            amount: toString(tender.amount),
          })),
        })

        /*
         * O recibo provisório (F197): o carrinho e o que o Edge respondeu.
         * Montado AGORA, antes de a tela limpar o carrinho para a próxima.
         */
        const receipt: ProvisionalReceipt = {
          reference: sale.reference,
          branchName: authSession?.context?.branchName ?? '',
          issuedAt: new Date(),
          operatorName: authSession?.user.displayName ?? null,
          lines: lines.map((line, index) => ({
            description: line.description,
            quantity: toString(line.quantity),
            unit: line.unit,
            unitPrice: line.unitPrice,
            lineTotal: totals?.lines[index]?.lineTotal ?? ZERO_MONEY,
          })),
          subtotal: totals?.subtotal ?? ZERO_MONEY,
          discount: totals?.discount ?? ZERO_MONEY,
          total: totals?.total ?? ZERO_MONEY,
          payments: tenders.map((tender) => ({
            method: tender.methodName,
            amount: tender.amount,
          })),
          changeDue:
            parseHumanAmount(sale.changeDue.replace('.', ','), MONEY_SCALE) ??
            ZERO_MONEY,
        }

        return {
          kind: 'edge',
          reference: sale.reference,
          total: sale.total,
          changeDue: sale.changeDue,
          receipt,
        } as const
      }

      const response = await posCheckout({
        cashSessionId: session?.id ?? '',
        customerPartyId: effectiveCustomer?.id ?? '',
        /*
         * Vazio significa "quem está operando", que é o caso comum e o padrão
         * do servidor. Mandar o próprio id seria a mesma coisa com mais bytes.
         */
        salespersonUserId: sellingUserId,
        // The document names no table, because every line names its own.
        priceListId: null,
        /*
         * Um dos dois, nunca os dois — o servidor recusa `discount_named_twice`
         * justamente porque adivinhar qual vale seria escolher em silêncio o
         * desconto de uma venda.
         */
        ...(discountMode === 'percent'
          ? {
              discountAmount: '0',
              discountPercent:
                typedDiscount === null ? null : toString(typedDiscount),
            }
          : {
              discountAmount:
                typedDiscount === null ? '0' : toString(typedDiscount),
              discountPercent: null,
            }),
        notes: null,
        lines: lines.map((line) => ({
          variantId: line.variantId,
          description: line.description,
          unit: line.unit,
          // Captured when the item was added, and sent as captured. The server
          // does not re-resolve it — ADR-0017's central invariant.
          unitPrice: toString(line.unitPrice),
          priceListId: line.priceListId,
          priceBasis: line.priceBasis,
          quantity: toString(line.quantity),
          discountAmount: '0',
        })),
        payments: tenders.map((tender) => ({
          paymentMethodId: tender.methodId,
          amount: toString(tender.amount),
        })),
        creditAuthorisationId,
      })

      return { kind: 'cloud', response } as const
    },
    onSuccess: async (response) => {
      setPaying(false)
      setFinished(response)
      setLines([])
      setDiscount('')
      setTerm('')
      setMatches([])
      /*
       * O cliente sai junto com o carrinho.
       *
       * Ficava. A venda terminava, o comprovante aparecia, e o nome do cliente
       * anterior continuava no alto da tela — de modo que a venda seguinte
       * começava com um cliente que ninguém escolheu, e ia para a conta dele.
       * É a informação mais cara de errar aqui: ela decide para quem a venda
       * foi e de quem é o crédito.
       */
      setCustomer(null)
      await queryClient.invalidateQueries({ queryKey: ['cash-session'] })
      await queryClient.invalidateQueries({ queryKey: ['sales-documents'] })
      // O número de "vendas aguardando" sobe agora, e não em 30 s (F197).
      await queryClient.invalidateQueries({ queryKey: ['edge', 'sync-status'] })
    },
    onError: async (error: unknown) => {
      /*
       * A venda não chegou à nuvem (F197): pergunta de novo se ela está no ar
       * AGORA, e não daqui a 30 s — é o que faz o balcão passar ao Edge
       * enquanto o cliente ainda está na frente.
       */
      if (error instanceof ApiUnreachableError) {
        await queryClient.invalidateQueries({ queryKey: HEALTH_QUERY_KEY })
      }
    },
  })

  /*
   * A loja com senha de cancelamento (F319): o servidor pede a senha, e o
   * motivo já digitado vai junto — só a senha é perguntada.
   */
  const [cancelAuthorising, setCancelAuthorising] = useState<string | null>(
    null
  )

  const cancelCompleted = useMutation({
    mutationFn: async ({
      reason,
      authorisationId,
    }: {
      reason: string
      authorisationId: string | null
    }) => {
      const document =
        finished?.kind === 'cloud' ? finished.response.document : undefined
      if (document === undefined) {
        throw new ApiError(409, 'no_sale', 'Nenhuma venda para cancelar.')
      }
      return await posCancelSale(document.id, {
        version: document.version,
        // O motivo que a pessoa digitou, e não um fixo: é o que responde
        // "por que essa venda sumiu?" no fechamento do caixa.
        reason,
        cashSessionId: session?.id ?? null,
        authorisationId,
      })
    },
    onSuccess: async () => {
      setConfirmingCompletedCancel(false)
      setNotice(null)
      setSuccess('Venda cancelada. Estoque e pagamentos foram estornados.')
      setFinished(null)
      await queryClient.invalidateQueries({ queryKey: ['cash-session'] })
      focusScan()
    },
    onError: (error: unknown, input) => {
      setConfirmingCompletedCancel(false)
      if (
        error instanceof ApiError &&
        error.code === 'authorisation_required' &&
        input.authorisationId === null
      ) {
        setCancelAuthorising(input.reason)
        return
      }
      setNotice(
        error instanceof ApiError
          ? error.message
          : 'Não foi possível cancelar a venda.'
      )
    },
  })

  const openTill = useMutation({
    mutationFn: async () => {
      const float = toString(
        parseHumanAmount(openingFloat, MONEY_SCALE) ?? ZERO_CASH
      )

      /*
       * Abrir turno no Edge é o caso da loja que já estava sem internet quando
       * o dia começou. O turno sobe na fila como qualquer venda, e a nuvem o
       * aplica por id — então abrir aqui e a internet voltar não produz dois.
       */
      if (onEdge) {
        return await edgeOpenSession({
          terminalId: normalizeTerminalId(terminalId),
          openingFloat: float,
          actorUserId: authSession?.user.id ?? null,
        })
      }

      return await openCashSession({
        terminalId: normalizeTerminalId(terminalId),
        ...(branchId ? { branchId } : {}),
        openingFloat: float,
        notes: null,
      })
    },
    onSuccess: async () => {
      setOpeningFloat('')
      await queryClient.invalidateQueries({ queryKey: ['cash-session'] })
      await queryClient.invalidateQueries({ queryKey: ['edge', 'sync-status'] })
    },
    onError: (error: unknown) => {
      setNotice(
        error instanceof ApiError || error instanceof EdgeError
          ? error.message
          : 'Não foi possível abrir o caixa.'
      )
    },
  })

  // -------------------------------------------------------------------------
  // The shortcuts
  // -------------------------------------------------------------------------
  //
  // Bound on the window rather than on a container: the operator's focus is
  // wherever the last action left it, and a shortcut that only works when the
  // right element is focused is a shortcut nobody trusts. Suppressed while a
  // dialog is open so F4 cannot open a customer picker on top of a payment.

  /*
   * `delivering` entra aqui (F105).
   *
   * Sem ele, com a pergunta do entregador aberta o F2 abria o recebimento POR
   * CIMA dela: dois diálogos empilhados, e a venda podendo ser concluída atrás
   * da pergunta sobre quem vai levar a mercadoria.
   */
  const dialogOpen = paying || choosingCustomer || delivering

  /*
   * O vendedor não tem gaveta, e o teclado é dele também.
   *
   * Antes o tratador inteiro desistia sem caixa aberto. Agora só as teclas
   * que mexem em dinheiro desistem — F2 é conferida onde é tratada, logo
   * abaixo —, e F9, F4, F6, F7, F8 e as de quantidade continuam valendo para
   * quem só monta o orçamento.
   */
  const keysEnabled = !(canTakeMoney && session === null)

  /*
   * `useScopedKeydown` e não `window.addEventListener` (F138): no Modo PC o
   * PDV divide a página com outras janelas, e a tecla só é dele quando foi
   * apertada nele. Fora do Modo PC é o mesmo listener no `window` de sempre.
   */
  useScopedKeydown((event: KeyboardEvent): void => {
    if (event.key === 'F1' || (event.key === '?' && term.length === 0)) {
      event.preventDefault()
      setHelping((value) => !value)
      return
    }

    if (dialogOpen || editingQuantity !== null) return

    if (event.key === 'F3') {
      event.preventDefault()
      // Focus drifts to the price-list select or a button and every other
      // shortcut still works, so the till feels fine right up to the moment
      // somebody scans and nothing happens.
      focusScan()
      return
    }

    if (event.key === 'F7' && lines.length > 0) {
      event.preventDefault()
      setQuantityDraft('')
      setEditingQuantity(selected)
      return
    }

    if (event.key === 'F8' && lines.length > 0 && canOverridePrice) {
      event.preventDefault()
      // Pre-filled with the current price, so the common edit is "change the
      // last two digits" rather than "retype the whole thing".
      setPriceDraft(toString(lines[selected]?.unitPrice ?? ZERO_MONEY))
      setEditingPrice(selected)
      return
    }

    /*
     * Recebimento: só de quem recebe, e só com a gaveta aberta.
     *
     * `noDefaultCustomer` também vale aqui, e não só no botão: um atalho que
     * passa por cima de um botão desabilitado é o botão não estar
     * desabilitado.
     */
    if (
      event.key === 'F2' &&
      lines.length > 0 &&
      session !== null &&
      noDefaultCustomer === null
    ) {
      event.preventDefault()
      setPaying(true)
      return
    }

    if (event.key === 'F4') {
      event.preventDefault()
      // Mesma regra do botão: sem nuvem não há carteira de clientes aqui.
      if (!till.capabilities.customer) return
      setChoosingCustomer(true)
      return
    }

    if (event.key === 'F9' && lines.length > 0 && noDefaultCustomer === null) {
      event.preventDefault()
      saveQuoteRef.current()
      return
    }

    /*
     * F10 abre a pergunta, e não despacha.
     *
     * Despachar tira mercadoria do estoque e levanta um título: uma tecla
     * que fizesse isso sozinha poria a venda na rua no nome de um motoboy
     * que ninguém escolheu.
     */
    if (event.key === 'F10' && lines.length > 0) {
      event.preventDefault()
      if (canDeliver) setDelivering(true)
      return
    }

    if (event.key === 'F6' && lines.length > 0) {
      event.preventDefault()
      setLines((currentLines) =>
        currentLines.filter((_, index) => index !== selected)
      )
      setSelected((index) => Math.max(index - 1, 0))
      return
    }

    /**
     * `+` and `−` only when the search box is empty.
     *
     * A code can contain a hyphen, and stealing it while somebody is typing
     * one would make the till impossible to use for half the catalogue.
     */
    if (term.length === 0 && (event.key === '+' || event.key === '=')) {
      event.preventDefault()
      changeQuantity(selected, ONE_UNIT)
      return
    }
    if (term.length === 0 && event.key === '-') {
      event.preventDefault()
      changeQuantity(selected, {
        units: -ONE_UNIT.units,
        scale: ONE_UNIT.scale,
      })
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      if (confirmingCancel) {
        setConfirmingCancel(false)
        focusScan()
        return
      }
      if (matches.length > 0) {
        setMatches([])
        return
      }
      /*
       * Esc also dismisses the suggestions, and it can only do that by
       * clearing what was typed — the list IS the term. Clearing the term is
       * also what somebody who typed the wrong thing wanted.
       */
      if (term.length > 0) {
        setTerm('')
        return
      }
      if (lines.length > 0) setConfirmingCancel(true)
    }
  }, keysEnabled)

  // -------------------------------------------------------------------------
  // Abertura de caixa — offered rather than a broken till
  // -------------------------------------------------------------------------

  /*
   * Offline, and honest about it.
   *
   * The shell now opens without a network (F017a), which creates a failure
   * mode that did not exist before: a till that looks ready and cannot sell.
   * Every operation here needs the API — the price, the stock, the ledger, the
   * cash session — so the screen says so once, plainly, instead of letting the
   * operator scan an item and discover it at the payment step with a customer
   * waiting.
   *
   * This is temporary by design. F017b gives the Edge a database and this
   * branch becomes "sell against the Edge" rather than "refuse".
   */
  /*
   * A pergunta "existe um Edge nesta máquina?" ainda não foi respondida.
   *
   * Só acontece com a nuvem fora do ar, e dura milissegundos — é uma consulta
   * a `127.0.0.1`. O indicador está aqui para não piscar a tela de recusa e
   * depois trocá-la pelo balcão funcionando, que é a sequência que faz alguém
   * concluir que o produto está quebrado.
   */
  if (till.isDeciding) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }

  /*
   * Sem nuvem e sem Edge: o único estado em que este balcão recusa.
   *
   * Era o comportamento de toda queda de internet. Agora é o de uma máquina
   * que não tem o Aeris Edge instalado — e a tela diz isso, porque é uma coisa
   * que se resolve, e não uma espera.
   */
  if (till.mode === 'none') {
    return (
      <Page>
        <PageHeader title="PDV" />

        <div
          role="status"
          className="flex flex-col gap-2 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-4"
        >
          <h2 className="text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
            Sem conexão com o servidor
          </h2>
          <p className="text-(length:--text-sm) text-(--text-secondary)">
            O Aeris abriu com o que estava guardado neste computador, mas este
            computador não tem o Aeris Edge — o programa que continua vendendo
            sem internet. Assim que a conexão voltar, o PDV volta sozinho.
          </p>
          <p className="text-(length:--text-sm) text-(--text-secondary)">
            Anote as vendas feitas neste período e lance depois. Para não
            precisar disso da próxima vez, fale com quem administra o Aeris na
            sua empresa sobre instalar o Edge no computador do balcão.
          </p>
        </div>
      </Page>
    )
  }

  // `isPending` is also what a DISABLED query reports, so this has to ask
  // whether there is anything to wait for. Without the guard, a machine with
  // no till chosen — a fresh browser profile — spins for ever instead of
  // showing the form that would let somebody choose one.
  if (canTakeMoney && hasTerminal && current.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }

  /*
   * A exigência de caixa aberto vale para quem recebe (F035).
   *
   * Para o vendedor, esta tela nunca deveria ter aparecido: ele não pode abrir
   * turno — `pos.operate` não é dele — então o formulário de abertura seria uma
   * porta trancada oferecida como se fosse a entrada.
   */
  if (canTakeMoney && session === null) {
    return (
      <Page>
        <PageHeader title="PDV" />

        {/* A till with no session is not an error state, it is the start of a
            shift. Offering the abertura here means the operator does not have
            to know that /caixa is a different screen. */}
        <form
          onSubmit={(event) => {
            event.preventDefault()
            openTill.mutate()
          }}
          noValidate
          className="flex flex-col gap-4 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-4"
        >
          <div>
            <h2 className="text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
              Abra o caixa para começar a vender
            </h2>
            <p className="text-(length:--text-sm) text-(--text-secondary)">
              Nenhum caixa aberto neste terminal. Informe o fundo de troco que
              está na gaveta.
            </p>
          </div>

          <div className="flex flex-wrap items-start gap-4">
            <TerminalSelect value={terminalId} onValueChange={setTerminalId} />

            <Field label="Fundo de troco" hint="Pode ser zero" className="w-56">
              {(field) => (
                <MoneyInput
                  {...field}
                  value={openingFloat}
                  autoFocus

                  onValueChange={setOpeningFloat}
                />
              )}
            </Field>
          </div>

          {notice === null ? null : (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {notice}
            </p>
          )}

          <div>
            {/* No till chosen means nothing to open. The server refuses
                it too — this only avoids asking. */}
            <Button type="submit" disabled={openTill.isPending || !hasTerminal}>
              Abrir caixa
            </Button>
          </div>
        </form>
      </Page>
    )
  }

  // -------------------------------------------------------------------------
  // The till
  // -------------------------------------------------------------------------

  /**
   * O aviso de cliente bloqueado, no momento em que ele importa (F033).
   *
   * O bloqueio já existia e já era mostrado — dentro do diálogo de pagamento,
   * quando a venda inteira já está montada e o cliente está encostado no balcão
   * esperando. Quem escolhe o cliente é quem precisa saber, e precisa saber
   * antes do primeiro item.
   *
   * O que o bloqueio faz continua sendo o que ele sempre fez: recusa fiado, não
   * recusa dinheiro. Recusar a venda inteira é outra decisão, precisa de saída
   * de supervisor, e não foi o que foi pedido — então o aviso informa em vez de
   * travar.
   */
  const blocked = credit.data?.blocked === true ? credit.data : null

  /*
   * A lista de atalhos diz a verdade sobre esta tela, e ela é diferente para
   * cada um dos dois lugares do balcão: ensinar F2 a quem não pode receber é
   * ensinar uma tecla que não faz nada.
   */
  const shortcuts = canTakeMoney
    ? SHORTCUTS
    : SHORTCUTS.filter(([keys]) => keys !== 'F2' && keys !== 'F10')

  return (
    <Page>
      <PageHeader
        title="PDV"
        description={
          session === null
            ? 'Orçamento — a mercadoria fica separada até o caixa receber.'
            : `${session.terminalId} · ${session.branchName}`
        }
        actions={
          <>
            {/*
              The table used to be chosen HERE, once, for the whole sale.
              F026 moved it onto the line: each product answers for its own
              price, because a counter that sells one item at atacado and the
              rest at varejo was choosing between two wrong carts. New items
              come in on the account's default list and can be moved
              individually.
            */}

            {/*
              The number the customer is looking at, and the operator is
              reading across a counter. Announced, because it changes without
              a reload.
            */}
            {/*
              O desconto da nota, ao lado do total e não numa tela à parte
              (F100). É onde a conversa acontece: "faz 10% pra ele" é dito
              olhando para o número que vai mudar.
            */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="pdv-desconto"
                className="text-(length:--text-xs) text-(--text-secondary)"
              >
                Desconto na nota
              </label>
              <div className="flex items-center gap-1">
                {/*
                  O campo segue o MODO — F179.

                  Era sempre `MoneyInput`, com a máscara de centavos: no modo
                  `%`, que é o padrão, digitar `10` para "faz 10%" virava
                  `0,10` na fita e `0,10%` na conta. A caixa dava dez por cento
                  e o sistema descontava dez centavos por cada cem reais — e a
                  fita nem denunciava, porque `0,10` é um número plausível.
                  Percentual não é dinheiro: não tem R$, não tem centavo
                  obrigatório, e o campo agora diz isso.
                */}
                {discountMode === 'percent' ? (
                  <Input
                    id="pdv-desconto"
                    inputMode="decimal"
                    value={discount}
                    placeholder="0"
                    className="w-24 text-right font-(--font-mono)"
                    onChange={(event) => {
                      setDiscount(event.target.value)
                    }}
                  />
                ) : (
                  <MoneyInput
                    id="pdv-desconto"
                    value={discount}
                    onValueChange={setDiscount}
                    className="w-24"
                  />
                )}
                <div
                  role="group"
                  aria-label="Desconto em reais ou percentual"
                  className="flex gap-1"
                >
                  {(
                    [
                      ['percent', '%'],
                      ['amount', 'R$'],
                    ] as const
                  ).map(([mode, label]) => (
                    <button
                      key={mode}
                      type="button"
                      aria-pressed={discountMode === mode}
                      onClick={() => {
                        /*
                          Trocar de modo LIMPA o campo (F179).

                          "10" quer dizer duas coisas muito diferentes nos dois
                          modos, e converter seria inventar a intenção de quem
                          digitou: R$ 10,00 não vira 10%. Redigitar custa dois
                          toques; um desconto errado custa a margem.
                        */
                        setDiscountMode(mode)
                        setDiscount('')
                      }}
                      className={`rounded-(--radius-sm) border px-2 py-0.5 text-(length:--text-xs) ${
                        discountMode === mode
                          ? 'border-(--brand-primary) text-(--brand-primary)'
                          : 'border-(--border-default) text-(--text-muted)'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {/*
                O valor que o percentual dá, porque é ele que a caixa fala em
                voz alta. Só quando há desconto: uma linha "− R$ 0,00" é ruído
                permanente no rodapé mais lido do produto.
              */}
              {totals !== null && totals.discount.units !== 0n ? (
                <span className="text-(length:--text-xs) text-(--text-muted)">
                  − {formatMoney(totals.discount)}
                </span>
              ) : null}
              {totals === null && typedDiscount !== null ? (
                <span className="text-(length:--text-xs) text-(--danger)">
                  Desconto maior que a venda
                </span>
              ) : null}
            </div>

            <div className="flex flex-col items-end">
              <span className="text-(length:--text-xs) text-(--text-secondary)">
                Total da venda
              </span>
              <output
                aria-label="Total da venda"
                aria-live="polite"
                className="font-(--font-mono) text-(length:--text-2xl) font-(--weight-semibold) text-(--text-primary)"
              >
                {formatMoney(total)}
              </output>
            </div>
          </>
        }
      />

      {/*
        Em que modo este balcão está, o tempo todo (F042).

        `role="status"` e não `alert`: não é um erro, é o estado de trabalho —
        e um alerta que aparece a cada queda de internet ensina a ignorar
        alertas.

        O número de vendas esperando é o sinal que faz alguém olhar a internet
        da loja: ele sobe e não desce até a conexão voltar. Fica na tela, e não
        num menu, porque quem precisa vê-lo tem um cliente na frente e não vai
        procurar.
      */}
      {onEdge ? (
        <div
          role="status"
          className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-(--radius-md) border border-(--warning) bg-(--warning-subtle) px-3 py-2"
        >
          <Badge tone="warning">Sem internet</Badge>
          <span className="text-(length:--text-sm) text-(--text-primary)">
            Vendendo pelo computador da loja. A venda sai sem número e sem
            cliente identificado, e sobe sozinha quando a conexão voltar.
          </span>
          {till.pending > 0 ? (
            <span className="font-(--font-mono) text-(length:--text-sm) text-(--text-secondary)">
              {till.pending === 1
                ? '1 venda aguardando'
                : `${String(till.pending)} vendas aguardando`}
            </span>
          ) : null}
          {/*
            Recusadas pela nuvem ao subir (F197): o Edge já contava, e a venda
            sumia calada. Com o número na tela, alguém procura o gerente.
          */}
          {till.refused > 0 ? (
            <Badge tone="danger">
              {till.refused === 1
                ? '1 venda recusada pela nuvem'
                : `${String(till.refused)} vendas recusadas pela nuvem`}
            </Badge>
          ) : null}
          {till.offline !== null &&
          till.offline.hoursOffline !== null &&
          till.offline.maxHours > 0 ? (
            <span className="text-(length:--text-sm) text-(--text-secondary)">
              {till.offline.canSell
                ? `Sem subir há ${String(Math.floor(till.offline.hoursOffline))} h — a venda para em ${String(till.offline.maxHours)} h.`
                : `Sem subir há ${String(Math.floor(till.offline.hoursOffline))} h: venda nova bloqueada até a internet voltar.`}
            </span>
          ) : null}
        </div>
      ) : null}

      {/*
        Parear com o Edge (F197): com a internet no ar, que é quando dá. Sem
        isto o balcão "tinha" modo offline e não vendia nada nele.
      */}
      {till.edgePairing === 'unpaired' ? (
        <EdgePairing terminalId={normalizeTerminalId(terminalId)} />
      ) : null}

      {/*
        A conta não tem Consumidor Final (F104).

        Fica na tela, e não só no `title` de um botão desabilitado, porque a
        causa está fora do alcance de quem opera o balcão: é cadastro que
        falta, e a pessoa precisa saber o que dizer a quem pode cadastrá-lo.
      */}
      {noDefaultCustomer !== null && finished === null ? (
        <div
          role="status"
          className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-(--radius-md) border border-(--warning) bg-(--warning-subtle) px-3 py-2"
        >
          <Badge tone="warning">Sem venda anônima</Badge>
          <span className="text-(length:--text-sm) text-(--text-primary)">
            {noDefaultCustomer}
          </span>
        </div>
      ) : null}

      {/*
        A barra do balcão: com quem é a venda, e de quem ela é (F035).
        
        O cliente e o vendedor estavam escondidos — um numa linha cinza de
        subtítulo, o outro num `select` no rodapé, depois dos botões. São as
        duas primeiras perguntas que o balcão faz, antes de qualquer produto,
        e agora estão onde o olho já está: em cima, grandes e clicáveis.
      */}
      {finished === null && (
        <div className="flex flex-wrap items-stretch gap-2">
          {/*
            Identificar o cliente é da nuvem (F042).

            O snapshot leva um cliente só, o Consumidor Final: descer a carteira
            inteira para cada PC de loja seria espalhar dados pessoais de toda a
            base para atender o caso raro de alguém querer nota em nome durante
            uma queda de internet.

            Desabilitado com o motivo no `title`, e não escondido: sumir faria a
            pessoa procurar o botão que ela usa todo dia.
          */}
          <button
            type="button"
            disabled={!till.capabilities.customer}
            title={
              till.capabilities.customer
                ? undefined
                : 'Sem internet, a venda sai para Consumidor Final.'
            }
            onClick={() => {
              setChoosingCustomer(true)
            }}
            className={cn(
              'flex min-w-64 flex-1 items-center gap-3 rounded-(--radius-md) border p-3 text-left',
              'border-(--border-subtle) bg-(--surface-raised)',
              'transition-colors hover:border-(--brand-primary) hover:bg-(--surface-hover)',
              'focus-visible:outline-2 focus-visible:outline-(--border-focus)',
              'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-(--border-subtle) disabled:hover:bg-(--surface-raised)',
              blocked !== null && 'border-(--danger)'
            )}
          >
            <UserRound
              aria-hidden="true"
              className="h-5 w-5 shrink-0 text-(--text-secondary)"
            />
            <span className="min-w-0 flex-1">
              <span className="block text-(length:--text-xs) text-(--text-muted)">
                Cliente (F4)
              </span>
              <span className="block truncate text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
                {effectiveCustomer?.name ?? '—'}
              </span>
              {/*
                O que já se sabe do cliente, à vista (F132). Crédito e vale eram
                buscados ao escolher o cliente e só apareciam dentro do diálogo
                de pagamento, depois de escolher "Fiado" ou "Vale".
              */}
              {customer === null ? null : (
                <span className="block truncate text-(length:--text-xs) text-(--text-secondary)">
                  {[
                    voucher.data !== undefined &&
                    voucher.data.balance !== '0.00'
                      ? `vale ${formatMoney(parseHumanAmount(voucher.data.balance.replace('.', ','), MONEY_SCALE) ?? ZERO_MONEY)}`
                      : null,
                    credit.data?.limit != null
                      ? `crédito ${formatMoney(parseHumanAmount(credit.data.available.replace('.', ','), MONEY_SCALE) ?? ZERO_MONEY)}`
                      : null,
                    (credit.data?.overdueCount ?? 0) > 0
                      ? `${String(credit.data?.overdueCount)} ${credit.data?.overdueCount === 1 ? 'vencido' : 'vencidos'}`
                      : null,
                    credit.data?.blocked === true ? 'bloqueado' : null,
                  ]
                    .filter((part) => part !== null)
                    .join(' · ')}
                </span>
              )}
            </span>
          </button>

          {!classic &&
          (allows('exchange.manage') || allows('exchange.request')) ? (
            <button
              type="button"
              onClick={() => {
                setExchanging(true)
              }}
              className="flex items-center gap-2 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-3 text-(length:--text-sm) text-(--text-primary) hover:bg-(--surface-sunken)"
            >
              <ArrowLeftRight aria-hidden="true" className="h-5 w-5" />
              Venda com troca
            </button>
          ) : null}

          <div className="flex min-w-56 flex-1 items-center gap-3 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-3">
            <BadgeCheck
              aria-hidden="true"
              className="h-5 w-5 shrink-0 text-(--text-secondary)"
            />
            <span className="min-w-0 flex-1">
              <span className="block text-(length:--text-xs) text-(--text-muted)">
                Vendedor
              </span>
              {salespeople.data !== undefined ? (
                <Select
                  aria-label="Vendedor desta venda"
                  value={chosenSalesperson}
                  onChange={(event) => {
                    chooseSalesperson(event.target.value)
                  }}
                >
                  {/* Sem opção vazia: toda venda tem um vendedor, e "não
                      informado" virava o operador em silêncio (F035). */}
                  {salespeople.data.items.map((person) => (
                    <option key={person.userId} value={person.userId}>
                      {person.displayName}
                    </option>
                  ))}
                </Select>
              ) : (
                <span className="block truncate text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
                  {authSession?.user.displayName ?? '—'}
                </span>
              )}
            </span>
          </div>
        </div>
      )}

      {awaitingExchange === null ? null : (
        <div
          role="status"
          className="flex items-start gap-3 rounded-(--radius-md) border border-(--border-subtle) bg-(--info-subtle) p-3 text-(length:--text-sm)"
        >
          <ArrowLeftRight aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="flex-1">
            {awaitingExchange.status === 'completed'
              ? `Troca${awaitingExchange.number === null ? '' : ` nº ${String(awaitingExchange.number)}`} concluída. O vale de ${formatMoney(parseHumanAmount(awaitingExchange.voucher.replace('.', ','), MONEY_SCALE) ?? ZERO_MONEY)} já está no saldo do cliente: na hora de pagar, escolha "Vale".`
              : `Venda esperando a troca${awaitingExchange.number === null ? '' : ` nº ${String(awaitingExchange.number)}`}: o caixa precisa concluir a troca antes de usar o vale.`}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setAwaitingExchange(null)
            }}
          >
            Fechar
          </Button>
        </div>
      )}

      {exchanging ? (
        <NewExchangeDialog
          onDone={(exchange) => {
            setExchanging(false)
            // A venda passa a ser do cliente da troca: o vale é dele.
            setCustomer({
              id: exchange.customerPartyId,
              name: exchange.customerName,
            })
            setAwaitingExchange({
              number: exchange.number,
              status: exchange.status,
              voucher: exchange.voucherCreditAmount,
            })
            void queryClient.invalidateQueries({
              queryKey: ['customer-credit', exchange.customerPartyId],
            })
          }}
          onClose={() => {
            setExchanging(false)
          }}
        />
      ) : null}

      {confirmingSeller === null ? null : (
        <ConfirmPersonDialog
          userId={confirmingSeller.userId}
          displayName={confirmingSeller.displayName}
          onConfirmed={(userId) => {
            setConfirmedSellers((current) => new Set([...current, userId]))
            setSalespersonUserId(userId)
            setConfirmingSeller(null)
          }}
          onClose={() => {
            setConfirmingSeller(null)
          }}
        />
      )}

      {blocked !== null && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) p-3"
        >
          <TriangleAlert
            aria-hidden="true"
            className="mt-0.5 h-4 w-4 shrink-0 text-(--danger)"
          />
          <p className="text-(length:--text-sm) text-(--danger)">
            <strong>Cliente bloqueado.</strong>{' '}
            {blocked.blockReason ?? 'Sem motivo registrado.'} Este cliente não
            leva fiado; à vista a venda segue normal.
          </p>
        </div>
      )}

      {finished === null ? (
        <>
          <div className="flex flex-wrap items-start gap-3">
            <Field
              className="min-w-64 flex-1"
              label="Código de barras, código ou nome"
              hint="Leia com o scanner, ou digite: as sugestões aparecem sozinhas. ↑ ↓ escolhem, Enter adiciona. Use 3*código para três unidades."
            >
              {(field) => (
                <Input
                  {...field}
                  ref={scanRef}
                  value={term}
                  autoFocus
                  autoComplete="off"
                  role="combobox"
                  aria-expanded={visible.length > 0}
                  aria-controls={visible.length > 0 ? 'pos-matches' : undefined}
                  aria-autocomplete="list"
                  aria-activedescendant={
                    visible.length > 0
                      ? `pos-match-${String(highlight)}`
                      : undefined
                  }
                  className="font-(--font-mono) text-(length:--text-lg)"
                  onChange={(event) => {
                    setTerm(event.target.value)
                    /*
                     * Digitou: a lista respondida era de outro termo (F212).
                     * Em 29/09 ela ficou na tela, o Enter pegou a primeira
                     * linha dela, e entrou uma bateria no lugar da tela.
                     */
                    if (matches.length > 0) setMatches([])
                    setAdded(null)
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      // A scanner ends its read with Enter. This field is not in
                      // a form precisely so that key can mean "add an item".
                      event.preventDefault()
                      /*
                       * Da lista, só se ela for do que está no campo (F212).
                       * A sugestão espera 150 ms; o leitor digita e dá Enter
                       * antes disso, e aí a lista na tela é do termo anterior.
                       */
                      if (visible.length > 0 && listIsCurrent) {
                        const chosen = visible[highlight]
                        if (chosen !== undefined) chooseMatch(chosen)
                        return
                      }
                      submitTerm()
                      return
                    }

                    if (event.key === 'ArrowDown') {
                      event.preventDefault()
                      if (visible.length > 0) {
                        setHighlighted((index) =>
                          Math.min(index + 1, visible.length - 1)
                        )
                      } else {
                        setSelected((index) =>
                          Math.min(index + 1, Math.max(lines.length - 1, 0))
                        )
                      }
                      return
                    }

                    if (event.key === 'ArrowUp') {
                      event.preventDefault()
                      if (visible.length > 0) {
                        setHighlighted((index) => Math.max(index - 1, 0))
                      } else {
                        setSelected((index) => Math.max(index - 1, 0))
                      }
                    }
                  }}
                />
              )}
            </Field>

            {/*
            Beside the search, not inside it. `3*código` is faster for somebody
            who knows it and stays in the hint; this is for the person holding
            four of something who looks for a field.
          */}
            <Field label="Qtd." className="w-24">
              {(field) => (
                <Input
                  {...field}
                  value={nextQuantity}
                  inputMode="decimal"
                  autoComplete="off"
                  className="text-right font-(--font-mono)"
                  onChange={(event) => {
                    setNextQuantity(event.target.value)
                  }}
                  onKeyDown={(event) => {
                    // Enter here means "now read the code", which is where the
                    // hand goes next.
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      focusScan()
                    }
                  }}
                />
              )}
            </Field>
          </div>

          {searchStatus === null ? null : (
            <p
              role="status"
              className="text-(length:--text-sm) text-(--text-secondary)"
            >
              {searchStatus}
            </p>
          )}

          {visible.length === 0 ? null : (
            <ul
              id="pos-matches"
              role="listbox"
              aria-label="Produtos encontrados"
              // F211: a lista rola dentro da tela, em vez de parar em oito.
              className="flex max-h-[60vh] flex-col gap-1 overflow-y-auto rounded-(--radius-md) border border-(--border-subtle) p-2"
            >
              {visible.map((match, index) => (
                <li
                  key={match.variantId}
                  id={`pos-match-${String(index)}`}
                  role="option"
                  aria-selected={index === highlight}
                  // A seta desce além do que cabe: o escolhido entra na tela.
                  ref={
                    index === highlight
                      ? (element) => {
                          element?.scrollIntoView({ block: 'nearest' })
                        }
                      : undefined
                  }
                >
                  <button
                    type="button"
                    tabIndex={-1}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-(--radius-md) px-3 py-2 text-left',
                      'text-(length:--text-sm) text-(--text-primary)',
                      // O mesmo conserto da linha da venda (F179).
                      index === highlight && 'bg-(--surface-selected)'
                    )}
                    onClick={() => {
                      chooseMatch(match)
                    }}
                  >
                    {/*
                      A foto entra aqui também, e aqui ela vale mais: é o
                      momento em que a pessoa escolhe entre oito nomes
                      parecidos com a peça na mão.
                    */}
                    <LineThumb
                      url={match.thumbUrl}
                      description={match.description}
                    />
                    <span className="min-w-0 flex-1 truncate font-(--weight-medium)">
                      {match.description}
                    </span>
                    {/*
                      F141: onde a peça fica nesta loja — o vendedor vai direto
                      à gaveta em vez de perguntar.
                    */}
                    {match.location === null ? null : (
                      <span
                        title="Localização nesta loja"
                        className="flex shrink-0 items-center gap-1 text-(length:--text-xs) text-(--text-secondary)"
                      >
                        <MapPin aria-hidden="true" className="h-3.5 w-3.5" />
                        {match.location}
                      </span>
                    )}
                    <span className="font-(--font-mono) text-(length:--text-xs) text-(--text-muted)">
                      {match.externalCode ?? match.sku}
                      {match.gtin === null ? '' : ` · ${match.gtin}`}
                    </span>

                    {/*
                      The price and the stock, on the row.
                      
                      The operator is choosing between eight products and the
                      price is most of how they choose; without it the only way
                      to find out was to add the item. And the stock is what
                      decides whether adding it can work at all — which the
                      till used to discover at the payment step, with the whole
                      cart already built.
                    */}
                    <span
                      className={cn(
                        'ml-auto font-(--font-mono) tabular-nums',
                        match.unitPrice === null && 'text-(--text-muted)'
                      )}
                    >
                      {match.unitPrice === null
                        ? 'sem preço'
                        : formatUnitPrice(match.unitPrice)}
                    </span>

                    {!match.tracksStock ? null : outOfStock(
                        match,
                        allowNegative
                      ) ? (
                      <Badge tone="danger">sem estoque</Badge>
                    ) : (
                      <span className="w-16 shrink-0 text-right font-(--font-mono) text-(length:--text-xs) text-(--text-muted) tabular-nums">
                        {asQuantity(match.onHand ?? '0')}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {success === null ? null : (
            <InlineAlert tone="success">{success}</InlineAlert>
          )}

          {notice === null ? null : (
            <p
              role="alert"
              className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-sm) text-(--danger)"
            >
              {notice}
            </p>
          )}

          {lines.length === 0 ? (
            <EmptyState
              icon={<ScanBarcode aria-hidden="true" className="h-8 w-8" />}
              title="Nenhum item na venda"
              description="Passe o produto no leitor ou digite o código e pressione Enter."
            />
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell className="w-14">
                      <VisuallyHidden>Foto</VisuallyHidden>
                    </TableHeaderCell>
                    {/*
                      O código do sistema antigo tem coluna própria.

                      É o nome que a loja usa para a peça — o cliente pede "o
                      1233" — e por isso não pode estar embaixo da descrição,
                      onde só é encontrado por quem já sabe que está lá.
                    */}
                    <TableHeaderCell className="w-24">Código</TableHeaderCell>
                    <TableHeaderCell>Produto</TableHeaderCell>
                    <TableHeaderCell>Un.</TableHeaderCell>
                    <TableHeaderCell numeric>Qtd.</TableHeaderCell>
                    <TableHeaderCell numeric>Preço</TableHeaderCell>
                    <TableHeaderCell numeric>Total</TableHeaderCell>
                    <TableHeaderCell numeric className="w-10">
                      <VisuallyHidden>Ações</VisuallyHidden>
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line, index) => (
                    <TableRow
                      key={line.key}
                      aria-current={index === selected ? 'true' : undefined}
                      /*
                        A linha escolhida PARECE escolhida — F179.

                        Era `--surface-raised`, que no tema claro é o mesmo
                        branco do fundo da tabela: a seleção não aparecia, e o
                        que estava sob o mouse (`--surface-hover`) parecia mais
                        selecionado que a seleção. `aria-selected` já é pintado
                        pelo `TableRow` com `--surface-selected`, que existe nos
                        três temas e ninguém usava.
                      */
                      aria-selected={index === selected}
                      // Clickable, because the arrows only reach a row from the
                      // scan field and a hand already on the mouse should not
                      // have to go back to the keyboard to pick a line.
                      onClick={() => {
                        setSelected(index)
                      }}
                      className="cursor-pointer"
                    >
                      <TableCell>
                        <LineThumb
                          url={line.thumbUrl}
                          description={line.description}
                        />
                      </TableCell>
                      <TableCell className="font-(--font-mono) text-(length:--text-sm) tabular-nums">
                        {/*
                          O `sku` é o que resta quando não houve importação:
                          um produto cadastrado aqui não tem código antigo, e
                          um traço não ajudaria ninguém a achar a peça.
                        */}
                        {line.externalCode ?? line.sku}
                      </TableCell>
                      <TableCell className="text-(--text-primary)">
                        {line.description}
                        {line.onHand === null ? null : (
                          <span
                            className={cn(
                              'block text-(length:--text-xs)',
                              compare(
                                line.quantity,
                                parseHumanAmount(
                                  line.onHand.replace('.', ','),
                                  QUANTITY_SCALE
                                ) ?? ZERO_MONEY
                              ) > 0
                                ? 'text-(--danger)'
                                : 'text-(--text-muted)'
                            )}
                          >
                            {compare(
                              line.quantity,
                              parseHumanAmount(
                                line.onHand.replace('.', ','),
                                QUANTITY_SCALE
                              ) ?? ZERO_MONEY
                            ) > 0
                              ? `só ${asQuantity(line.onHand)} em estoque`
                              : `em estoque ${asQuantity(line.onHand)}`}
                          </span>
                        )}
                        {line.priceBasis === 'quantity-tier' ? (
                          <Badge tone="info" className="ml-2">
                            faixa por quantidade
                          </Badge>
                        ) : null}

                        {/*
                          Which table this price came from, and the way to move
                          it. Under the name rather than in a column of its
                          own: it is an answer about the price, and it is read
                          far less often than it is ignored.
                        */}
                        <select
                          aria-label={`Tabela de preço de ${line.description}`}
                          value={line.priceListId ?? ''}
                          disabled={repriceLine.isPending}
                          onClick={(event) => {
                            event.stopPropagation()
                          }}
                          onChange={(event) => {
                            repriceLine.mutate({
                              index,
                              sku: line.sku,
                              priceListId: event.target.value,
                            })
                          }}
                          className={cn(
                            'mt-0.5 block max-w-full rounded-(--radius-sm) border-none bg-transparent',
                            'px-0 py-0 text-(length:--text-xs) text-(--text-muted)',
                            'hover:text-(--text-secondary) focus-visible:outline-none',
                            'focus-visible:ring-2 focus-visible:ring-(--border-focus)'
                          )}
                        >
                          {/*
                            The account default is not offered as a choice once
                            a line exists: the line was priced by a REAL list
                            and naming it is what makes the row honest. It is
                            still the option a new line arrives on.
                          */}
                          {line.priceListId === null ? (
                            <option value="">Padrão da conta</option>
                          ) : null}
                          {(priceLists.data?.priceLists ?? []).map((list) => (
                            <option key={list.id} value={list.id}>
                              {list.name}
                            </option>
                          ))}
                        </select>
                      </TableCell>
                      <TableCell>{line.unit}</TableCell>
                      <TableCell numeric className="font-(--font-mono)">
                        {editingQuantity === index ? (
                          <input
                            autoFocus
                            inputMode="decimal"
                            aria-label={`Quantidade de ${line.description}`}
                            value={quantityDraft}
                            onChange={(event) => {
                              setQuantityDraft(event.target.value)
                            }}
                            onBlur={() => {
                              setEditingQuantity(null)
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault()
                                const next = parseHumanAmount(
                                  quantityDraft,
                                  QUANTITY_SCALE
                                )
                                // An unreadable draft leaves the line alone
                                // rather than zeroing it: the operator meant to
                                // set a quantity, not to delete the item.
                                if (next !== null) setQuantity(index, next)
                                setEditingQuantity(null)
                                focusScan()
                                return
                              }
                              if (event.key === 'Escape') {
                                event.preventDefault()
                                event.stopPropagation()
                                setEditingQuantity(null)
                                focusScan()
                              }
                            }}
                            className={cn(
                              'w-20 rounded-(--radius-sm) border border-(--border-focus)',
                              'bg-(--surface) px-1 py-0.5 text-right',
                              'font-(--font-mono) text-(length:--text-sm) outline-none'
                            )}
                          />
                        ) : (
                          /*
                           * Clicável, como o preço ao lado.
                           *
                           * Era texto puro: a quantidade só se editava por F7,
                           * `+`/`−` ou removendo a linha e lançando de novo —
                           * e o preço, na coluna vizinha, era um botão. Quem
                           * clicava num e não no outro concluía, com razão,
                           * que a quantidade não se muda depois de lançada.
                           * Corrigir uma quantidade é a edição MAIS comum do
                           * balcão, e era a única sem porta para o mouse.
                           */
                          <button
                            type="button"
                            // Nomeado pelo que FAZ. Sem isto o nome acessível
                            // do botão é o número, que se lê como um valor e
                            // não como algo para pressionar.
                            aria-label={`Alterar a quantidade de ${line.description}`}
                            title="Alterar a quantidade deste item (F7)"
                            className={cn(
                              'rounded-(--radius-sm) px-1 font-(--font-mono) tabular-nums',
                              'underline decoration-dotted underline-offset-4',
                              'hover:bg-(--surface)'
                            )}
                            onClick={(event) => {
                              event.stopPropagation()
                              setSelected(index)
                              setEditingQuantity(index)
                              setQuantityDraft(
                                toString(line.quantity).replace(/\.?0+$/, '')
                              )
                            }}
                          >
                            {toString(line.quantity).replace(/\.?0+$/, '')}
                          </button>
                        )}
                      </TableCell>
                      <TableCell numeric className="font-(--font-mono)">
                        {editingPrice === index ? (
                          <input
                            autoFocus
                            inputMode="decimal"
                            aria-label={`Preço de ${line.description}`}
                            value={priceDraft}
                            onChange={(event) => {
                              setPriceDraft(event.target.value)
                            }}
                            onBlur={() => {
                              setEditingPrice(null)
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault()
                                const next = parseHumanAmount(
                                  priceDraft,
                                  MONEY_SCALE
                                )
                                // An unreadable draft leaves the price alone.
                                // Zeroing it would be a giveaway nobody typed.
                                if (next !== null) setUnitPrice(index, next)
                                setEditingPrice(null)
                                focusScan()
                                return
                              }
                              if (event.key === 'Escape') {
                                event.preventDefault()
                                event.stopPropagation()
                                setEditingPrice(null)
                                focusScan()
                              }
                            }}
                            className={cn(
                              'w-24 rounded-(--radius-sm) border border-(--border-focus)',
                              'bg-(--surface) px-1 py-0.5 text-right',
                              'font-(--font-mono) text-(length:--text-sm) outline-none'
                            )}
                          />
                        ) : (
                          <button
                            type="button"
                            // Not a button when the operator may not depart:
                            // an affordance that always refuses teaches people
                            // to distrust the screen.
                            disabled={!canOverridePrice}
                            // Named for what it DOES. Without this the button's
                            // accessible name is the price itself, which reads
                            // as a value rather than as something to press.
                            aria-label={`Alterar o preço de ${line.description}`}
                            title={
                              canOverridePrice
                                ? 'Alterar o preço deste item (F8)'
                                : 'Você não tem permissão para alterar o preço'
                            }
                            className={cn(
                              'font-(--font-mono) tabular-nums',
                              canOverridePrice &&
                                'rounded-(--radius-sm) px-1 underline decoration-dotted underline-offset-4 hover:bg-(--surface)'
                            )}
                            onClick={(event) => {
                              event.stopPropagation()
                              if (!canOverridePrice) return
                              setSelected(index)
                              setEditingPrice(index)
                              setPriceDraft(toString(line.unitPrice))
                            }}
                          >
                            {formatUnitPrice(toString(line.unitPrice))}
                          </button>
                        )}
                        {line.priceBasis === 'manual' ? (
                          // The badge is the way back: an override somebody
                          // cannot undo is one they have to remove the line to
                          // escape, with the customer watching.
                          <button
                            type="button"
                            // Named for what it DOES. Without this the button's
                            // accessible name is the badge's text — "preço
                            // alterado" — which announces a state and offers no
                            // clue that pressing it undoes one.
                            aria-label={`Voltar ao preço da tabela de ${line.description}`}
                            title="Voltar ao preço da tabela"
                            className="ml-2 align-middle"
                            onClick={(event) => {
                              event.stopPropagation()
                              clearOverride(index)
                            }}
                          >
                            <Badge tone="warning">preço alterado</Badge>
                          </button>
                        ) : null}
                      </TableCell>
                      <TableCell
                        numeric
                        className="font-(--font-mono) font-(--weight-medium)"
                      >
                        {formatMoney(
                          totals?.lines[index]?.lineTotal ?? ZERO_MONEY
                        )}
                      </TableCell>
                      <TableCell numeric>
                        {/* F6 removes the selected line and always did; the
                            button is for the hand that is already on the mouse
                            and for anybody who never found F6. */}
                        <button
                          type="button"
                          aria-label={`Remover ${line.description}`}
                          onClick={(event) => {
                            event.stopPropagation()
                            removeLine(index)
                            focusScan()
                          }}
                          className={cn(
                            'rounded-(--radius-sm) p-1 text-(--text-muted)',
                            'hover:bg-(--danger-subtle) hover:text-(--danger)'
                          )}
                        >
                          <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {confirmingCancel ? (
            <div
              role="alertdialog"
              aria-label="Cancelar a venda"
              className="flex flex-wrap items-center justify-between gap-3 rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2"
            >
              <span className="text-(length:--text-sm) text-(--text-primary)">
                Descartar {lines.length} {lines.length === 1 ? 'item' : 'itens'}{' '}
                desta venda?
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setConfirmingCancel(false)
                    focusScan()
                  }}
                >
                  Continuar a venda
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    setLines([])
                    setSelected(0)
                    setConfirmingCancel(false)
                    setSuccess('Venda cancelada.')
                    focusScan()
                  }}
                >
                  Descartar
                </Button>
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {/*
              Finalizar é do caixa (ADR-0027). Para o vendedor o botão não fica
              desabilitado: ele não existe. Um botão cinza é uma promessa de que
              um dia aquilo vai funcionar para quem o vê, e não vai.
            */}
            {canTakeMoney ? (
              <Button
                variant="primary"
                disabled={lines.length === 0 || noDefaultCustomer !== null}
                title={noDefaultCustomer ?? undefined}
                onClick={() => {
                  setPaying(true)
                }}
                leadingIcon={<Wallet aria-hidden="true" className="h-4 w-4" />}
              >
                Finalizar venda (F2)
              </Button>
            ) : null}
            {/*
              Salvar e imprimir são um botão só, e é o que o balcão faz: o
              orçamento nasce, a peça fica segurada, e o papel sai. A reserva
              está presa à gravação e não ao clique em imprimir — reimprimir
              não pode segurar a peça duas vezes.
            */}
            <Button
              variant={canTakeMoney ? 'secondary' : 'primary'}
              disabled={
                lines.length === 0 ||
                saveQuote.isPending ||
                // Orçamento não existe no Edge: ele reserva mercadoria e gera
                // número, e as duas coisas são da nuvem.
                !till.capabilities.quote ||
                noDefaultCustomer !== null
              }
              title={
                !till.capabilities.quote
                  ? 'Sem internet, não dá para gravar orçamento.'
                  : (noDefaultCustomer ?? undefined)
              }
              onClick={() => {
                saveQuote.mutate()
              }}
              leadingIcon={
                saveQuote.isPending ? (
                  <Spinner className="h-4 w-4" />
                ) : (
                  <Printer aria-hidden="true" className="h-4 w-4" />
                )
              }
            >
              {saveQuote.isPending ? 'Salvando…' : 'Orçamento (F9)'}
            </Button>
            {/*
              Entregar é do caixa, como Finalizar: a mercadoria sai e nasce um
              título a receber. Para o vendedor o botão não existe — um botão
              cinza é uma promessa de que um dia aquilo vai funcionar para quem
              o vê, e não vai.
            */}
            {canTakeMoney ? (
              <Button
                variant="secondary"
                disabled={
                  lines.length === 0 ||
                  deliver.isPending ||
                  deliveryBlocked !== null
                }
                title={deliveryBlocked ?? undefined}
                onClick={() => {
                  setDelivering(true)
                }}
                leadingIcon={
                  deliver.isPending ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    <Bike aria-hidden="true" className="h-4 w-4" />
                  )
                }
              >
                {deliver.isPending ? 'Despachando…' : 'Entregar (F10)'}
              </Button>
            ) : null}
            <Button
              variant="secondary"
              onClick={() => {
                setHelping((value) => !value)
              }}
              leadingIcon={<Keyboard aria-hidden="true" className="h-4 w-4" />}
            >
              Atalhos (F1)
            </Button>
          </div>
        </>
      ) : (
        // ---------------------------------------------------------------
        // Venda concluída
        // ---------------------------------------------------------------
        <section
          aria-label="Venda concluída"
          className="flex flex-col gap-3 rounded-(--radius-md) border border-(--success) bg-(--success-subtle) p-4"
        >
          <div className="flex flex-wrap items-baseline gap-3">
            <Badge tone="success">Venda concluída</Badge>
            <span className="font-(--font-mono) text-(length:--text-sm) text-(--text-secondary)">
              {finished.kind === 'cloud'
                ? `Nº ${formatDocumentNumber(finished.response.document.number)}`
                : /*
                   * Sem número, e dito assim.
                   *
                   * A numeração é da nuvem — um Edge que inventasse a própria
                   * série produziria dois documentos com o mesmo número no dia
                   * em que dois balcões vendessem offline ao mesmo tempo. O
                   * operador precisa saber disso ANTES de prometer uma nota ao
                   * cliente que está na frente dele.
                   */
                  `Ref. ${finished.reference.slice(0, 8)} · sem nº até subir`}
            </span>
          </div>

          <div className="flex flex-wrap gap-8">
            <div className="flex flex-col">
              <span className="text-(length:--text-xs) text-(--text-muted)">
                Total
              </span>
              <span className="font-(--font-mono) text-(length:--text-xl) text-(--text-primary)">
                {formatMoney(
                  parseHumanAmount(
                    (finished.kind === 'cloud'
                      ? finished.response.settlement.total
                      : finished.total
                    ).replace('.', ','),
                    MONEY_SCALE
                  ) ?? ZERO_MONEY
                )}
              </span>
            </div>
            {/*
              O troco é a conta que a nuvem devolve pronta.

              No Edge ele não aparece: o operador acabou de digitar o valor
              recebido no diálogo de pagamento, e repetir uma subtração que a
              tela não conferiu seria oferecer um número com cara de conferido.
            */}
            <div className="flex flex-col">
              <span className="text-(length:--text-xs) text-(--text-muted)">
                Troco
              </span>
              <output
                aria-label="Troco"
                className="font-(--font-mono) text-(length:--text-xl) text-(--text-primary)"
              >
                {formatMoney(
                  parseHumanAmount(
                    (finished.kind === 'cloud'
                      ? finished.response.settlement.changeDue
                      : finished.changeDue
                    ).replace('.', ','),
                    MONEY_SCALE
                  ) ?? ZERO_MONEY
                )}
              </output>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {/*
              Focused as soon as the receipt appears, so the next sale starts
              with Enter.

              Without it the till is keyboard-driven right up to the moment a
              sale completes and then asks for the mouse — once per customer,
              which over a shift is the difference between a fast counter and a
              slow one.
            */}
            <Button ref={newSaleRef} variant="primary" onClick={startNewSale}>
              Nova venda
            </Button>

            {/*
              The receipt the customer is handed. Offered rather than printed
              automatically: a counter that prints on every sale wastes a roll
              on the customers who wave it away, and F2-Enter-Enter must stay
              the whole of a cash sale.
            */}
            {/*
              Só venda da nuvem tem recibo: a do balcão offline não tem número
              até subir, e o botão abria nada.
            */}
            <Button
              variant="secondary"
              onClick={() => {
                setPrinting(true)
              }}
              leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
            >
              {finished.kind === 'cloud'
                ? 'Imprimir recibo'
                : 'Imprimir recibo provisório'}
            </Button>
            {/* Hiding only: the API refuses this independently for anybody
                without `sales.cancel`. */}
            {canCancel && till.capabilities.cancel ? (
              <Button
                variant="secondary"
                disabled={cancelCompleted.isPending}
                onClick={() => {
                  setConfirmingCompletedCancel(true)
                }}
              >
                Cancelar esta venda
              </Button>
            ) : null}
            {/*
              Cancelar estorna estoque e dinheiro: pergunta antes, e pede o
              motivo (F132).
            */}
            <ConfirmDialog
              open={confirmingCompletedCancel}
              onOpenChange={setConfirmingCompletedCancel}
              title="Cancelar esta venda?"
              description="O estoque volta para a prateleira e os pagamentos são estornados."
              confirmLabel="Cancelar a venda"
              cancelLabel="Voltar"
              tone="danger"
              loading={cancelCompleted.isPending}
              reason={{
                label: 'Motivo',
                placeholder: 'Ex.: cliente desistiu',
                required: true,
              }}
              onConfirm={(reason) => {
                cancelCompleted.mutate({
                  reason: reason ?? '',
                  authorisationId: null,
                })
              }}
            />
            {cancelAuthorising === null || finished.kind !== 'cloud' ? null : (
              <AuthorisationDialog
                title="Cancelar esta venda"
                description="Cancelar pede a senha de cancelamento da loja, ou a do gerente."
                action="sales.cancel"
                targetType="sales_document"
                targetId={finished.response.document.id}
                fixedReason={cancelAuthorising}
                onAuthorised={(authorisationId) => {
                  const reason = cancelAuthorising
                  setCancelAuthorising(null)
                  cancelCompleted.mutate({ reason, authorisationId })
                }}
                onClose={() => {
                  setCancelAuthorising(null)
                }}
              />
            )}
          </div>
        </section>
      )}

      {/*
        All of them, not the first five.
        
        The module docstring says a shortcut nobody can discover is a shortcut
        nobody uses, and then `.slice(0, 5)` hid seven of the twelve — including
        the one that removes a line and the one that cancels the sale.
      */}
      <footer className="flex flex-wrap gap-x-4 gap-y-1 border-t border-(--border-subtle) pt-2 text-(length:--text-xs) text-(--text-muted)">
        {shortcuts.map(([keys, what]) => (
          <span key={keys}>
            <kbd className="rounded-(--radius-sm) bg-(--surface-sunken) px-1 font-(--font-mono) text-(--text-secondary)">
              {keys}
            </kbd>{' '}
            {what}
          </span>
        ))}
      </footer>

      {quote !== null && (
        <QuoteDialog
          quote={quote}
          paper={paper}
          onPaperChange={(next) => {
            setPaper(next)
            rememberPaper(next)
          }}
          onClose={() => {
            setQuote(null)
          }}
        />
      )}

      {printing && finished !== null ? (
        <ReceiptDialog
          sale={
            finished.kind === 'cloud' ? finished.response : finished.receipt
          }
          terminal={terminalId.trim().length === 0 ? null : terminalId}
          paper={paper}
          onPaperChange={(next) => {
            setPaper(next)
            rememberPaper(next)
          }}
          onClose={() => {
            setPrinting(false)
          }}
        />
      ) : null}

      {helping ? (
        <section
          aria-label="Atalhos do PDV"
          className="rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-4"
        >
          <h2 className="mb-2 text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
            Atalhos
          </h2>
          <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
            {shortcuts.map(([keys, what]) => (
              <div key={keys} className="flex gap-2">
                <dt className="w-24 shrink-0 font-(--font-mono) text-(length:--text-sm) text-(--text-secondary)">
                  {keys}
                </dt>
                <dd className="text-(length:--text-sm) text-(--text-primary)">
                  {what}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}

      {choosingCustomer ? (
        <PosCustomerDialog
          onChoose={(party) => {
            setCustomer({
              id: party.id,
              name: partyDisplayName(party),
            })
            setChoosingCustomer(false)
            focusScan()
          }}
          onClose={() => {
            setChoosingCustomer(false)
            focusScan()
          }}
        />
      ) : null}

      {delivering ? (
        <CourierDialog
          title="Quem leva esta entrega?"
          pending={deliver.isPending}
          error={
            deliver.error instanceof ApiError ? deliver.error.message : null
          }
          onChoose={(courierPartyId) => {
            deliver.mutate(courierPartyId)
          }}
          onQueue={
            hasFeature('entregas-rota')
              ? () => {
                  deliver.mutate(null)
                }
              : undefined
          }
          onClose={() => {
            setDelivering(false)
            deliver.reset()
            focusScan()
          }}
        />
      ) : null}

      {paying ? (
        <PosPaymentDialog
          total={total}
          methods={methods.data?.methods ?? []}
          credit={credit.data ?? null}
          voucherBalance={voucher.data?.balance ?? null}
          voucherLookup={customerLookup(voucher, {
            hasCustomer: customer !== null,
            allowed: allows('exchange.read'),
          })}
          creditLookup={customerLookup(credit, {
            hasCustomer: customer !== null,
            allowed: allows('finance.read'),
          })}
          pending={checkout.isPending}
          error={
            checkout.error instanceof ApiError ||
            checkout.error instanceof EdgeError
              ? checkout.error.message
              : checkout.error instanceof ApiUnreachableError
                ? 'A nuvem não respondeu. O caixa confere o computador da loja e tenta de novo.'
                : null
          }
          onConfirm={(tenders, creditAuthorisationId) => {
            checkout.mutate({ tenders, creditAuthorisationId })
          }}
          onClose={() => {
            setPaying(false)
            focusScan()
          }}
          /*
           * A saída do recebimento para a entrega (F105).
           *
           * Só existe para quem opera o caixa, como o botão do rodapé: a saída
           * levanta um título a receber, e quem responde por dinheiro a
           * receber é quem recebe (ADR-0027). `undefined` esconde a saída
           * inteira em vez de mostrá-la cinza.
           */
          onDeliver={
            canTakeMoney
              ? () => {
                  setPaying(false)
                  setDelivering(true)
                }
              : undefined
          }
          deliveryBlocked={deliveryBlockedInDialog}
        />
      ) : null}
    </Page>
  )
}
